import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Lock, ChevronLeft, RefreshCw, AlertCircle } from "lucide-react";
import { api } from "@/services/api";
import type { AddMaterialsResult, OemOffersLine } from "@/services/api";
import { StatusPill } from "@/components/StatusPill";
import { MaterialTable } from "@/components/MaterialTable";
import { MaterialPicker } from "@/components/MaterialPicker";
import { QuotePdfPreview } from "@/components/QuotePdfPreview";
import { CustomerDetailsModal } from "@/components/CustomerDetailsModal";
import { useAdminView, useCanEditCustomersOrFalse } from "@/auth/AuthContext";
import { ChangeNotificationStack, UndoBar } from "@/components/UndoBar";
import { RemovedLineToast } from "@/components/RemovedLineToast";
import { SidePanel } from "@/components/SidePanel";
import { PriceExplainPanel } from "@/components/PriceExplainPanel";
import { SummaryStrip } from "@/components/SummaryStrip";
import { BackendPriceStrip, VARIANCE_PCT, GP_PCT } from "@/components/BackendPriceStrip";
import type { LiveStatus, PricingMode } from "@/components/MaterialTable";
import {
  discardDraft,
  adoptSavedShipments,
  adoptSavedPricing,
  detailToQuoteShipments,
  getStock,
  withStockLevels,
  livePricingCorrespondsTo,
  materialToBackendLine,
  removeLineFromRows,
  restoreLineToRows,
  savedQuoteToLivePricing,
  savedQuoteStoreKey,
  savedQuoteToMaterials,
  savedQuoteToPricingContext,
  savedQuoteToQuote,
} from "@/services/api";
import type { BackendCompanySettings, CurrencyOption, LivePricedLine, SavedQuoteDetail } from "@/services/api";
import { SaveQuoteBanner } from "@/components/SaveQuoteBanner";
import { PoDocuments } from "@/components/PoDocuments";
import { PoComparison } from "@/components/PoComparison";
import { StartFromPoStrip } from "@/components/StartFromPoStrip";
import { QuoteFinalisedBanner } from "@/components/QuoteFinalisedBanner";
import { FreightBasisBanner } from "@/components/FreightBasisBanner";
import { UnpricedBanner } from "@/components/UnpricedBanner";
import { ExampleDataBanner } from "@/components/ExampleDataBanner";
import type { SaveStatus } from "@/components/SaveQuoteBanner";
import type { CustomerPartner, CustomerSnapshot, QuoteCustomer } from "@/types";
import { formatDate, formatPercent, formatRupees } from "@/lib/format";
import { devToolsEnabled } from "@/lib/devTools";
import { pricingSignatureOf } from "@/lib/pricingSignature";
import { readStoredPageSize, storePageSize, type PageSize } from "@/lib/linePager";
import { useCanEditQuote } from "@/auth/AuthContext";
import { materialToBackendSaveLine, oemLinkFromMatch, oemWarningsFor, type OemQuoteMatch } from "@/services/api";
import { OemQuoteDialog } from "@/components/OemQuoteDialog";
import { registerUnsavedWork } from "@/lib/sessionExpiry";
import { oemValidityCap } from "@/lib/oemValidityCap";
import { saveDraft, findDraftFor, resolveWorkingReference, listFxRates, zeroRateMeansNoRate, copyQuote, newWorkingReference,
  requestApproval, withdrawApproval, approveQuote, rejectQuote,
  markQuoteWon, markQuoteLost, resetQuoteStatus, type OutcomeActionResult, reviseQuote,
  getQuoteTax, getWarehouses, warehouseOptionLabel, type Warehouse } from "@/services/api";
import { ApprovalPanel, type ApprovalRefusal } from "@/components/ApprovalPanel";
import { OutcomePanel } from "@/components/OutcomePanel";
import { OemConfirmationsPanel, oemFinaliseWarning } from "@/components/OemConfirmationsPanel";
import {
  useOemDealData, OemRequestsBlock, OemOrderingBlock, OemTrackingBlock,
  requestOemPrices, markOemRequestSent, orderableByOem,
} from "@/components/OemOrdersPanel";
import { DealProgressBar, NextStepCard, DealSection, sectionId } from "@/components/DealProgress";
import { OemCompareDialog, OemOffersSummary } from "@/components/OemCompareDialog";
import {
  useFulfilmentData, ReceiveGoodsBlock, DeliverBlock, CustomsBlock, CloseOutBlock,
} from "@/components/FulfilmentPanels";
import {
  deriveDealSteps, deriveNextAction, focusStep, type DealInputs, type NextAction, type StepKey,
} from "@/lib/dealProgress";
import type { GateRefusal } from "@/services/api";
import type { OemConfirmationsView } from "@/services/api";
import type { OutcomeStatus, QuoteOutcome, QuoteStatus, QuoteTax } from "@/types";

// quote-won-lost: the header pill for each outcome the backend reports.
const OUTCOME_PILL: Record<OutcomeStatus, QuoteStatus> = {
  open: "Open", won: "Won", lost: "Lost", expired: "Expired", superseded: "Superseded",
};
import { staleRateNotice, staleRateText, todayIso } from "@/services/fxInForce";
import type { FxRate } from "@/types";
import { finaliseBlockers, finaliseBlockedMessage } from "@/services/finaliseGuard";
import type { WorkingReferenceLookup } from "@/services/api";
import { createSaveLane } from "@/services/saveLane";
import type { SaveLane } from "@/services/saveLane";
import { freightBasisProblems, freightBasisMessage } from "@/services/freightBasisGuard";
import {
  unpricedProblems, unpricedMessage, pricableLines, freightPendingProblems, freightPendingMessage,
} from "@/services/unpricedGuard";
import { FreightPendingBanner } from "@/components/FreightPendingBanner";
import { exampleDataProblem, exampleDataMessage } from "@/services/mockQuoteGuard";
import { recomputeAll, computeWarningsSummary, computeEngineMarginSummary } from "@/services/pricing";
import type { PricingContext, Material, QuoteShipment } from "@/types";
import { colors, semanticColors, radius, spacing, typography, iconSize, iconStroke, heights, layout, shadows, overlayMaxWidth } from "@/styles/tokens";

// EVERYTHING A REOPENED QUOTE PUTS BACK ON SCREEN, decided in one place.
//
// Both entry points -- /saved-quotes/:id and a restored draft -- go through
// this, because they had drifted: the saved path seeded the staleness
// fingerprint and the draft path did not, so a restored draft handed the
// table engine numbers and the guard blanked them on the very next render.
// One function means the two cannot disagree about what a reopen restores.
//
// THE ENGINE NUMBERS ARE PROVEN BEFORE THEY ARE SHOWN. livePricingCorrespondsTo
// has to agree that the stored prices belong to these inputs; when it does not,
// this returns an empty map and "idle", which is the blank columns and the
// recompute prompt. A stored number is never rendered as live on trust --
// showing one that no longer matches its inputs is the failure the whole
// live/est. split exists to prevent.
// EXPORTED FOR ITS TEST ONLY. Nothing else imports it: the point of this
// function is that both reopen paths go through the one copy, and a second
// caller elsewhere would undo that. It is exported rather than reached through
// a rendered page because what needs pinning is the mapping itself.
export function restoreFromSavedQuote(
  detail: SavedQuoteDetail,
  fallbackCtx: PricingContext,
): {
  mode: PricingMode;
  ctx: PricingContext;
  // The quote's REAL shipments, rebuilt from the records the backend returned
  // — id -> key, so the next save updates them in place instead of creating
  // copies. PricingContext above carries no shipments of any kind: the
  // display blob that used to sit in it went with the allocator (BF5d-3).
  shipments: QuoteShipment[];
  materials: Material[];
  rows: Material[];
  live: Map<number, LivePricedLine>;
  verified: boolean;
  signature: string;
} {
  const mode: PricingMode = detail.pricing_mode ?? "domestic";
  // A quote saved before the context was stored, or one whose stored blob is
  // malformed, keeps whatever the panel currently has rather than resetting.
  const ctx = savedQuoteToPricingContext(detail) ?? fallbackCtx;
  // DERIVED ONCE and handed to both halves. The panel's shipment list and the
  // lines' shipmentKeys have to come from the same array, or a line can end
  // up naming a key the panel never minted -- the dropdown would then show a
  // blank for an assignment that is really there.
  const shipments = detailToQuoteShipments(detail);
  const materials = savedQuoteToMaterials(detail, shipments);
  // The restored context no longer reaches this: recomputeAll derives
  // netPrice and the placeholder chain from the row alone (BF5d-3). `ctx` is
  // still restored and returned below because SidePanel reads its FX rate and
  // negotiation buffer.
  const rows = recomputeAll(materials);

  const stored = savedQuoteToLivePricing(detail);
  const verified = livePricingCorrespondsTo(rows, stored, detail, mode);

  return {
    mode,
    ctx,
    // Empty on a domestic quote and on anything saved before shipments
    // existed, which is the same thing the panel shows for "none".
    //
    // The lines are reattached from this same list -- see savedQuoteToMaterials
    // above. Both halves land in one batch, which the seeding-order rule
    // requires: a render holding restored lines but not their shipments would
    // show every assignment as blank until the next one.
    shipments,
    materials,
    rows,
    live: verified ? stored : new Map(),
    verified,
    // Empty when unverified, so the guard's own comparison stays meaningful:
    // it is the fingerprint of what the live columns are showing, and they
    // are showing nothing.
    signature: verified ? pricingSignatureOf(rows, mode) : "",
  };
}

function LabelValue({
  label, children, testid, large,
}: { label: string; children: React.ReactNode; testid?: string; large?: boolean }) {
  return (
    // nowrap keeps each figure whole: the header wraps by moving whole
    // metrics onto the next line, never by breaking "Valid Until" in half.
    <div className="flex flex-col items-end" data-testid={testid} style={{ whiteSpace: "nowrap" }}>
      <div style={{
        fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder,
        textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide, fontWeight: typography.fontWeight.medium,
      }}>{label}</div>
      <div style={{
        fontSize: large ? typography.fontSize.xl : typography.fontSize.base,
        fontWeight: large ? typography.fontWeight.semibold : typography.fontWeight.regular,
        color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums", marginTop: spacing[2],
      }}>
        {children}
      </div>
    </div>
  );
}

export const UNSAVED_CHANGES_TEXT = "You have unsaved changes.";

export default function QuoteDetailPage() {
  // Two routes render this page:
  //   /quotes/:id             a mock quote from the in-memory store
  //   /saved-quotes/:savedId  a quote read back out of Odoo
  //
  // In saved mode the lines and their engine numbers come from the backend
  // instead of the mocks. Nothing else about the screen changes, which is
  // the point: a reopened quote is the same workspace, populated from the
  // database. It is READ-ONLY on arrival — loading a saved quote never
  // re-saves or re-prices it. Saving again is a deliberate button press.
  const { id = "", savedId } = useParams();
  const isSavedMode = savedId !== undefined;

  // Whether to show the engine-verification panel. Read per render rather
  // than once at module load so ?dev=1 works on a route the user navigated
  // to client-side. See lib/devTools.ts — this hides a panel, it does not
  // disable anything.
  const showDevTools = devToolsEnabled();

  const [saved, setSaved] = useState<SavedQuoteDetail | null>(null);
  const [savedStatus, setSavedStatus] = useState<LiveStatus>(
    isSavedMode ? "loading" : "idle",
  );
  const [savedLoadError, setSavedLoadError] = useState("");
  const [savedNotFound, setSavedNotFound] = useState(false);

  const quote = useMemo(
    () => (isSavedMode ? savedQuoteToQuote(saved) : api.getQuote(id)),
    [isSavedMode, saved, id],
  );

  // ⚠️ THE ONE KEY FOR THE PER-QUOTE MATERIAL STORE. Every writer on this
  // screen -- the picker's add, the remove, the undo, and the reopen seed --
  // goes through this single value, so they cannot land in different buckets.
  //
  // Saved mode keys on the BACKEND ID for two reasons, both of which were live
  // bugs. `id` is "" on /saved-quotes/:savedId (that route has no :id), so the
  // remove and undo below were writing to one orphan bucket shared by every
  // saved quote. And `detail.name` is not unique -- live holds Q-2026-0001
  // seven times and the mock quotes reuse the same names -- so the picker,
  // which keyed on quote.quoteNo, landed a reopened quote's picks in a MOCK
  // quote's bucket, which then fell through to the 180-row mockMaterials array.
  //
  // Mock mode is unchanged: storeKey === id === quoteNo, exactly what
  // createQuote seeds under.
  //
  // "" only while the read is in flight, which never reaches a writer: the
  // loading, error and !quote gates all return before the table renders.
  const storeKey = isSavedMode
    ? (saved ? savedQuoteStoreKey(saved.quote_id) : "")
    : id;
  // THE SHIPMENTS ARGUMENT IS NOT OPTIONAL IN PRACTICE, even though the
  // signature defaults it. Without it every line here reads as unassigned,
  // because resolveLineShipmentKey has no list to match a shipment_id against.
  //
  // This is the table's SEED (via displayMaterials). The load effect normally
  // overwrites it with restoreFromSavedQuote's result, which does pass the
  // shipments -- so the omission was masked rather than harmless. Any path
  // that renders before that effect settles, or if it fails, showed every
  // assignment as blank. Both call sites now derive the list the same way.
  const initialMaterials = useMemo(
    () => (isSavedMode
      ? (saved ? savedQuoteToMaterials(saved, detailToQuoteShipments(saved)) : [])
      : api.getMaterials(storeKey)),
    [isSavedMode, saved, storeKey],
  );

  // The quote's pricing path. Absent means domestic, mirroring the backend,
  // which defaults a missing pricing_mode the same way. Derived once so the
  // table's columns, the pricing request and the line mapping cannot
  // disagree about which path this quote is on.
  //
  // The override exists because `quote` is recomputed from the store (or from
  // the loaded saved quote) on every render, so the toggle needs somewhere of
  // its own to hold the user's choice. It also covers saved quotes, which are
  // not in quotesStore at all and so cannot be written back to.
  const [pricingModeOverride, setPricingModeOverride] = useState<PricingMode | null>(null);
  const pricingMode: PricingMode = pricingModeOverride ?? quote?.pricingMode ?? "domestic";

  // A different quote's mode must never carry over to this one.
  useEffect(() => { setPricingModeOverride(null); }, [id, savedId]);

  // Flipping the mode changes which columns exist and which inputs the engine
  // is given, so any existing engine numbers describe a question that is no
  // longer being asked. Nothing is blanked here directly: pricingMode is part
  // of pricingSignature, so the existing staleness guard sees the change and
  // clears the live columns, exactly as it does for a qty edit.
  const changePricingMode = (mode: PricingMode) => {
    if (mode === pricingMode) return;
    setPricingModeOverride(mode);
    // Persist onto the stored quote so the choice survives navigating away
    // and back within the tab. A no-op for a saved quote, which is not in the
    // store — the override above is what holds the mode in that case.
    if (!isSavedMode) api.setQuotePricingMode(id, mode);
  };

  // SUPPLIER CURRENCY -- the same override+fallback shape as the mode above,
  // and for the same reason: a saved quote is not in quotesStore, so an
  // override is the only place a choice can live for one.
  //
  // Unlike the mode there is NO fallback default. An unrecorded currency
  // stays undefined all the way to the payload, where it is omitted. The
  // field exists to distinguish "we know" from "we do not", and defaulting
  // here would destroy exactly that.
  const [supplierCurrencyOverride, setSupplierCurrencyOverride] =
    useState<string | null>(null);
  const supplierCurrency = supplierCurrencyOverride ?? quote?.supplierCurrency;

  useEffect(() => { setSupplierCurrencyOverride(null); }, [id, savedId]);

  // THE CUSTOMER CHOSEN ON THIS SCREEN, for a draft that arrived without one.
  //
  // ⚠️ A THIRD OVERRIDE ON EXACTLY THE TERMS OF THE TWO ABOVE, and for the
  // same reason: `quote` is recomputed from the store or from the loaded
  // saved quote on every render, and a saved quote is not in quotesStore at
  // all, so a choice made here needs somewhere of its own to live.
  //
  // ⚠️ IT IS NOT SAVED HERE. It feeds quoteForSave, so the EXISTING autosave
  // carries it on its next tick -- one write path, already the owner of the
  // save token, the supersession rule and the finalised latch. A second
  // writer for one field is how two writers start disagreeing about a record.
  //
  // Only a quote that HAS no customer can be given one this way; nothing on
  // this screen changes a customer that is already set.
  const [customerOverride, setCustomerOverride] = useState<QuoteCustomer | undefined>(undefined);

  useEffect(() => { setCustomerOverride(undefined); }, [id, savedId]);

  // DISPATCH FROM (warehouse-master, 2026-10-01). A choice made here; NOT saved
  // here -- it rides quoteForSave on the existing autosave, exactly like
  // customerOverride. undefined = untouched (the save omits the key, so the
  // server keeps what it holds, or applies the company default on create).
  const [warehouseOverride, setWarehouseOverride] = useState<number | null | undefined>(undefined);
  useEffect(() => { setWarehouseOverride(undefined); }, [id, savedId]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [defaultWarehouseLabel, setDefaultWarehouseLabel] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void getWarehouses().then((r) => { if (live && r.ok && r.warehouses) setWarehouses(r.warehouses); });
    void api.getCompanySettingsFromBackend().then((r) => {
      if (live && r.ok && r.settings) setDefaultWarehouseLabel(r.settings.default_warehouse_label ?? null);
    });
    return () => { live = false; };
  }, []);

  // THE CUSTOMER'S DETAILS AS JUST EDITED ON THIS SCREEN (customer-details-
  // on-quote). The edit is written to the CUSTOMER record by its own route,
  // never to this quote -- so it is NOT folded into quoteForSave and the
  // autosave never sees it. It only refreshes what this screen and its PDF
  // show until the quote is next read back. Sales/Admin only (cosmetic: the
  // route refuses a Viewer with a 403).
  const [editedEntity, setEditedEntity] = useState<CustomerPartner | undefined>(undefined);
  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(false);
  const canEditCustomers = useCanEditCustomersOrFalse();
  useEffect(() => { setEditedEntity(undefined); setCustomerDetailsOpen(false); }, [id, savedId]);
  // state-from-gstin: the customer's State disagrees with its GSTIN (saved).
  const [customerGstinNote, setCustomerGstinNote] = useState("");
  useEffect(() => { setCustomerGstinNote(""); }, [id, savedId]);

  // THE CUSTOMER AS ISSUED BY A FINALISE PRESSED IN THIS TAB. The save
  // response carries it; `quote` is only re-read on the next open, so without
  // this the document would print the live customer until then -- and a
  // customer edit made in the same tab after finalising would reach a
  // document that must not change (customer-snapshot-at-finalise).
  const [issuedSnapshot, setIssuedSnapshot] = useState<CustomerSnapshot | undefined>(undefined);
  useEffect(() => { setIssuedSnapshot(undefined); }, [id, savedId]);

  // THIS QUOTE HAS JUST BEEN DELETED, so there is nothing left to render.
  //
  // ⚠️ A FLAG AND <Navigate>, DELIBERATELY NOT useNavigate(). This screen
  // already redirects with the <Navigate> component and already imports it;
  // reaching for the hook instead broke 101 tests in 15 suites at once,
  // because they mock react-router-dom with only the members this file used
  // to need. A component that is only CONSTRUCTED when the flag is set costs
  // those mocks nothing.
  const [deleted, setDeleted] = useState(false);
  // discard-draft (owner 2026-09-30): the confirmation, and the server's answer.
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardBusy, setDiscardBusy] = useState(false);
  const [discardError, setDiscardError] = useState("");

  // MARGIN APPROVAL (margin-approval). `approvalRefusal` holds the backend's
  // two figures after a finalise press was refused below the limit; the
  // reload counter re-reads the quote after an approval action.
  const [approvalRefusal, setApprovalRefusal] = useState<ApprovalRefusal | undefined>(undefined);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalError, setApprovalError] = useState("");
  const [approvalReload, setApprovalReload] = useState(0);
  // M3.1: an outcome change (Won/Lost/reset) re-reads the OEM panels, whose
  // Accepted state and customer dates follow the quote's outcome.
  const [outcomeTick, setOutcomeTick] = useState(0);
  useEffect(() => { setApprovalRefusal(undefined); setApprovalError(""); }, [id, savedId]);

  // QUOTE OUTCOME (quote-won-lost). The outcome as the LAST ACTION on this
  // screen reported it -- a Won/Lost/reset press, or a PO save that marked
  // the quote Won -- until the quote is next read. Every figure is untouched
  // by these, so nothing else is re-read.
  const [outcomeOverride, setOutcomeOverride] = useState<QuoteOutcome | undefined>(undefined);
  const [outcomeBusy, setOutcomeBusy] = useState(false);
  const [outcomeError, setOutcomeError] = useState("");
  useEffect(() => { setOutcomeOverride(undefined); setOutcomeError(""); }, [id, savedId]);

  // COPY TO NEW DRAFT (copy-quote). Set to the new draft's id once the
  // backend has made it; the screen then moves there with <Navigate>, the
  // same way it leaves a deleted quote (see the note below on useNavigate).
  const [copiedToId, setCopiedToId] = useState<number | undefined>(undefined);
  // approval-request-fresh-quote: a request made from /quotes/:id opens the
  // saved record, where the pending lock is read and enforced. Same flag +
  // <Navigate> pattern as the two above, for the same reason.
  const [approvalOpenId, setApprovalOpenId] = useState<number | undefined>(undefined);
  // ⚠️ RESET ON THE ROUTE CHANGE, like copiedToId below. Both routes render
  // this same component, so it is NOT remounted: a flag left set keeps
  // rendering <Navigate> on the page it just opened -- a blank screen.
  useEffect(() => { setApprovalOpenId(undefined); }, [id, savedId]);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState("");
  const adminView = useAdminView();
  useEffect(() => { setCopiedToId(undefined); setCopyError(""); }, [id, savedId]);

  useEffect(() => { setDeleted(false); }, [id, savedId]);

  // Fetched once, so the picker offers what the backend has actually
  // activated rather than a list baked into this bundle.
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  useEffect(() => {
    let alive = true;
    api.listCurrencies().then((rows) => { if (alive) setCurrencies(rows); });
    return () => { alive = false; };
  }, []);

  // THE PUBLISHED FX SCHEDULE, read only to NOTICE a stale rate on a draft --
  // see fxNotice below. Never used to price: the backend resolves the rate the
  // engine multiplies by. A failed read leaves this empty, and an empty
  // schedule makes the note say nothing rather than guess.
  const [fxSchedule, setFxSchedule] = useState<FxRate[]>([]);
  useEffect(() => {
    let alive = true;
    void listFxRates().then((result) => {
      if (alive && result.ok) setFxSchedule(result.rates ?? []);
    });
    return () => { alive = false; };
  }, []);

  // Storing a currency changes NO price -- it names what conversion_rate
  // converts from and is not itself an engine input -- so unlike
  // changePricingMode this deliberately does not disturb the live columns.
  const changeSupplierCurrency = (code: string) => {
    setSupplierCurrencyOverride(code);
    if (!isSavedMode) api.setQuoteSupplierCurrency(id, code);
  };

  // THE QUOTE AS A SAVE PATH MUST SEE IT: this screen's effective mode and
  // currency folded onto it.
  //
  // ⚠️ WITHOUT THIS A SAVED QUOTE SILENTLY DROPS BOTH, and reports success
  // while doing it. `quote` is a memo: in saved mode it is a fresh literal
  // built by savedQuoteToQuote from the response, and the toggle cannot write
  // to it -- a saved quote is not in quotesStore, which is why the overrides
  // exist at all. Both save paths derive their payload from `quote`, so the
  // screen read "import" while the payload declared "domestic", and the
  // supplier_currency key was omitted as absent. The save succeeded and the
  // user was told so.
  //
  // Only `/quotes/:id` escaped it, and by luck rather than design: there
  // getQuote returns the live store object and the setters mutate it in
  // place, so the payload happened to see the change.
  //
  // A SPREAD, NEVER A HAND-BUILT LITERAL. odooQuoteId and odooQuoteName decide
  // whether a save is an update and which reference it keeps; enumerating
  // fields here would drop them the first time one is added.
  //
  // Folded HERE rather than inside the services on purpose: the services keep
  // one derivation each and no read-site changes, so there is no second gate
  // to drift out of step with the first.
  const quoteForSave = quote && {
    ...quote,
    pricingMode,
    // Stays UNDEFINED when nothing is recorded -- never "". The payload omits
    // the key on a falsy value, and the field's whole job is to distinguish
    // "we know" from "we do not".
    supplierCurrency,
    // ⚠️ BOTH FIELDS OR NEITHER. `endClient` is the rule-matching key for
    // brand locks and discounts and `customer` is what the save sends as ids;
    // NewQuoteModal derives the first from the second for exactly this reason,
    // and setting one here without the other is how they drift apart.
    ...(customerOverride?.entity
      ? { customer: customerOverride, endClient: customerOverride.entity.name }
      : {}),
    // warehouse-master: only a choice made on this screen is sent.
    ...(warehouseOverride !== undefined ? { dispatchWarehouseChoice: warehouseOverride } : {}),
  };

  const [ctx, setCtx] = useState<PricingContext>(() => api.getPricingContext(id));
  // Both load effects (saved-quote and draft-restore) seed `rows` through
  // recomputeAll, which needs the current context -- but neither may RE-RUN
  // when the context changes: re-running would refetch the quote every time a
  // SidePanel slider moved. Reading it off a ref says that out loud, instead
  // of a dependency array that quietly disagrees with the lint rule.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  // THE QUOTE'S REAL SHIPMENTS, held parallel to ctx and deliberately NOT
  // inside it. PricingContext is panel settings the backend stores opaquely
  // and reads with nothing; these are database records that price the lines.
  // Putting them in the same object would mean one save key carrying both a
  // thing the engine uses and a thing it ignores -- which is close to what
  // the old display blob was, an array of invented shipments living inside
  // that opaque JSON and allocating freight nobody had booked.
  //
  // Starts empty on every quote. A saved one fills it from the response; a
  // new one fills it when someone adds a shipment.
  const [shipments, setShipments] = useState<QuoteShipment[]>([]);
  // Read by the debounced autosave for the same reason as ctxRef: the timer
  // closes over whatever was current when it was scheduled.
  const shipmentsRef = useRef(shipments);
  shipmentsRef.current = shipments;

  // NO GATE HERE, DELIBERATELY. Both call sites hand `shipmentsRef.current`
  // straight to the save, and whether that block is actually emitted is
  // decided once, inside the service, by shipmentsFor() -- from the very
  // `mode` and `supplier_currency` the payload declares.
  //
  // There WAS a gate here, and removing it is the point rather than a
  // tidy-up. It read `pricingMode === "import" && supplierCurrency` off this
  // component while the service read the quote object, and on a saved quote
  // those two disagreed: the screen said import, the payload said domestic,
  // and the backend refused every save. Keeping a second opinion beside
  // shipmentsFor() is how that class of bug grows back, so there is exactly
  // one decision point and this is not it.
  //
  // The ref rather than the state value, for the same reason ctxRef exists:
  // the debounced autosave must send what is on screen when the timer fires,
  // not what was there when it was scheduled.

  const [rows, setRows] = useState<Material[]>(() => recomputeAll(initialMaterials));

  // THE ROWS AS THEY STAND NOW, for the save handlers -- which close over the
  // rows they SENT and answer some hundreds of milliseconds later. An edit in
  // that window leaves the two different, and writing the sent copy back would
  // revert it. Render-assigned like ctxRef and shipmentsRef above, for the same
  // reason: a handler needs the current value, not the one its closure caught.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // BF5d-2 -- THE SHIPMENT THE ALLOCATOR WOULD REFUSE, for the banner.
  //
  // ⚠️ DERIVED ON EVERY RENDER, NOT LATCHED, and that is the whole difference
  // from `finalisedElsewhere` below. Finalising is a one-way door, so it
  // latches and never clears. This condition is the opposite: changing the
  // basis in the panel, or assigning a line that carries the dimension, fixes
  // it -- so the banner has to go away by itself when it does. Deriving makes
  // "it clears when fixed" a property of the structure rather than something a
  // later edit has to remember to do.
  //
  // ⚠️ THE MODE COMES OFF `quoteForSave`, NOT off `pricingMode`. They are the
  // same value today, and the note on shipmentsRef above records what happened
  // the last time this screen kept a second opinion about what the payload
  // declares. Reading the object that is actually sent is what makes them
  // unable to disagree.
  //
  // This is the RENDER-TIME view -- what is on screen right now. The two save
  // seams recompute against the exact values they are about to send, which can
  // differ from this for a frame; each is answering its own question.
  // LINES WITH NO PRICE YET. Derived on every render, like the freight-basis
  // problems above and for the same reason: typing a price clears it, so the
  // banner must clear itself rather than waiting for a flag to be reset.
  const unpricedIssues = useMemo(() => unpricedProblems(rows), [rows]);
  const unpricedBlockMessage = useMemo(
    () => unpricedMessage(unpricedIssues),
    [unpricedIssues],
  );

  // shipment-price-gap: lines on a shipment still awaiting allocation. Derived
  // on every render; they are left out of the price request and named here.
  const freightPendingBlockMessage = useMemo(
    () => freightPendingMessage(freightPendingProblems(rows, pricingMode)),
    [rows, pricingMode],
  );

  // C1: IS THIS QUOTE SHOWING THE SHARED EXAMPLE LINES RATHER THAN ITS OWN?
  //
  // Derived on every render, like the two guards around it. It is read from
  // the store rather than held in state because seedReopenedQuote and
  // addMaterialsToQuote can both make it stop being true DURING the life of
  // the screen -- a restored draft or a first pick gives this quote a list of
  // its own -- and both already force the render that re-reads it.
  //
  // ⚠️ `rows` IS IN THE DEPS FOR THAT REASON, not because the rows decide the
  // answer. The predicate is the store entry; `rows` is what moves when the
  // store does, so it is what makes this recompute at the right moment.
  const exampleDataIssue = useMemo(
    () => exampleDataProblem(
      quote?.quoteNo ?? "",
      {
        isSavedMode,
        hasOwnMaterialStore: api.hasOwnMaterialStore(storeKey),
        isExampleQuoteNo: api.isExampleQuoteNo(quote?.quoteNo ?? ""),
      },
      rows,
    ),
    [quote?.quoteNo, isSavedMode, storeKey, rows],
  );
  const exampleDataBlockMessage = useMemo(
    () => exampleDataMessage(exampleDataIssue),
    [exampleDataIssue],
  );

  const freightBasisIssues = useMemo(
    () => freightBasisProblems(shipments, rows, quoteForSave?.pricingMode),
    [shipments, rows, quoteForSave?.pricingMode],
  );
  const freightBasisBlockMessage = useMemo(
    () => freightBasisMessage(freightBasisIssues),
    [freightBasisIssues],
  );

  const [changeCount, setChangeCount] = useState(0);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  // app review P2-13 (owner 2026-09-29): on a narrow screen the analysis
  // panel stacks BELOW the quote, collapsed by default, and a note says
  // editing works best on a larger screen. Follows the breakpoint, like the
  // sidebar's own auto-collapse in Layout.tsx.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${layout.quoteStackBelow - 1}px)`);
    const apply = (matches: boolean) => {
      setNarrow(matches);
      if (matches) setPanelCollapsed(true);
    };
    apply(query.matches);
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [showOnlyInStock, setShowOnlyInStock] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // COSMETIC ONLY. A Viewer cannot price or save, so the controls that do
  // it are hidden rather than offered and then refused. The backend is what
  // actually stops them -- these same actions answer 403 regardless.
  // Own-only, Admin-any. A quote that has never been persisted has no owner
  // yet, so it stays editable; the backend is what actually refuses.
  const canEditByRole = useCanEditQuote(quote?.ownerUid);
  // margin-approval: a quote awaiting approval is LOCKED for everyone. The
  // backend refuses every save of it; this only stops offering the edits.
  const awaitingApproval = quote?.approval?.state === "pending";
  const canEdit = canEditByRole && !awaitingApproval;
  // GP % PER QUOTE (gp-per-quote, owner 2026-09-23). The value SENT with
  // every price and save is the quote's own GP -- the stored one until
  // someone types another. It reaches the engine only through the existing
  // pricing-config input (gp_pct on the payload); nothing here prices.
  // Existing quotes show what they are stored with (28 on all of them today).
  const [gpText, setGpText] = useState<string | null>(null);
  useEffect(() => { setGpText(null); }, [id, savedId]);
  const storedGp = quote?.gpPct ?? GP_PCT;
  const gpInput = gpText ?? String(storedGp);
  const gpParsed = Number(gpInput);
  const gpValid = gpInput.trim() !== "" && Number.isFinite(gpParsed) && gpParsed > 0 && gpParsed < 100;
  const gpPct = gpValid ? gpParsed : storedGp;
  // The limit comes from the server's approval block; below it, the note.
  const approvalLimit = quote?.approval?.thresholdPct ?? null;
  // warehouse-master: what "Dispatch from" shows. A choice made here wins;
  // otherwise the stored warehouse (undefined on a quote not yet read back,
  // where the server applies the company default on its first save).
  const dispatchValue: number | null | undefined =
    warehouseOverride !== undefined ? warehouseOverride : quote?.warehouseId;
  const dispatchReadonly = (quote?.tax?.dispatchLabel && !quote?.tax?.legacy && quote?.status !== "Draft")
    ? quote.tax.dispatchLabel
    : (quote?.warehouseLabel ?? null);
  const gpNeedsApproval = gpValid && approvalLimit !== null && gpParsed < approvalLimit;
  // copy-quote: offered on a SAVED quote to Sales and Admin -- an Admin any,
  // Sales their own or a finalised one. Mirrors _may_view on the backend.
  const canCopy = canEditCustomers && quote?.odooQuoteId !== undefined
    && (adminView.isAdmin || quote?.ownerUid === adminView.uid || quote?.status !== "Draft");
  const copyToNewDraft = async () => {
    const sourceId = quote?.odooQuoteId;
    if (sourceId === undefined) return;
    setCopyBusy(true);
    setCopyError("");
    const result = await copyQuote(sourceId, newWorkingReference());
    setCopyBusy(false);
    if (!result.ok || result.quoteId === undefined) {
      setCopyError(result.error ?? "The copy was not made.");
      return;
    }
    setCopiedToId(result.quoteId);
  };
  // quote-revisions: Revise a finalised Open/Expired quote into a revision
  // draft. The backend decides everything (one open draft, not Won/Lost/
  // Superseded, own-or-Admin); on success the screen moves to the draft the
  // same way a copy does.
  const [reviseBusy, setReviseBusy] = useState(false);
  const [reviseError, setReviseError] = useState("");
  useEffect(() => { setReviseError(""); }, [id, savedId]);
  const reviseThisQuote = async () => {
    const sourceId = quote?.odooQuoteId;
    if (sourceId === undefined) return;
    setReviseBusy(true);
    setReviseError("");
    const result = await reviseQuote(sourceId, newWorkingReference());
    setReviseBusy(false);
    if (!result.ok || result.quoteId === undefined) {
      setReviseError(result.error ?? "No revision was made.");
      return;
    }
    setCopiedToId(result.quoteId);
  };
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  // Company identity for the document, from Odoo. Fetched when the preview is
  // opened rather than on every quote render -- it is only ever needed here.
  // The GET is all-roles, so a sales or viewer user generating a document is
  // not a permissions problem.
  const [companySettings, setCompanySettings] = useState<BackendCompanySettings | undefined>(undefined);
  const [flashLineNos, setFlashLineNos] = useState<number[]>([]);
  // QUOTE LINES PAGINATION (quote-lines-pagination). Held HERE, not in the
  // table: MaterialTable is remounted by `key` on every add, delete, load and
  // adoption, and its own state would reset to page 1 each time. The page size
  // is remembered per browser; the page is per quote (reset below).
  const [linePage, setLinePage] = useState(0);
  const [linePageSize, setLinePageSizeState] = useState<PageSize>(() => readStoredPageSize());
  const setLinePageSize = (size: PageSize) => { storePageSize(size); setLinePageSizeState(size); setLinePage(0); };
  // A line to bring into view (just added / restored); the table opens its
  // page and clears the request.
  const [revealLine, setRevealLine] = useState<{ lineNo: number } | null>(null);
  // QUOTE LINES FILTER (quote-lines-filter): the text typed above the table.
  // Held here for the same remount reason; NOT remembered after leaving the
  // quote (reset below, and gone when this screen unmounts). It never narrows
  // a total: see MaterialTable's `filter` prop.
  const [lineFilter, setLineFilter] = useState("");
  useEffect(() => { setLinePage(0); setRevealLine(null); setLineFilter(""); }, [id, savedId]);
  const undoRef = useRef<(() => void) | null>(null);

  // Real-backend pricing for this quote's lines. Lives here rather than in
  // BackendPriceStrip because it drives MaterialTable's live columns, so the
  // page has to own it. Keyed by lineNo; an absent entry means that line has
  // no engine number and its live columns stay blank.
  const [liveStatus, setLiveStatus] = useState<LiveStatus>("idle");
  const [liveError, setLiveError] = useState("");
  const [livePricing, setLivePricing] = useState<Map<number, LivePricedLine>>(new Map());

  // A different quote's prices must never appear under this one.
  useEffect(() => {
    setLivePricing(new Map());
    setLiveStatus("idle");
    setLiveError("");
    // Keyed on savedId too: switching between saved quotes must not leave
    // one quote's engine numbers showing under another's lines.
  }, [id, savedId]);

  // Which line's price explanation is open. Held here rather than in
  // MaterialTable because the panel it drives is a sibling of the table, and
  // it reads both `rows` and `livePricing`, which this page owns.
  const [explainLineNo, setExplainLineNo] = useState<number | null>(null);
  useEffect(() => { setExplainLineNo(null); }, [id, savedId]);

  // The panel explains a stored engine result, so it closes the moment that
  // result goes away — a failed re-price, or the staleness guard dropping the
  // numbers after an edit. Leaving it open would keep a chain on screen that
  // no longer describes the line, which is the same wrong-number problem the
  // live columns blank themselves to avoid.
  useEffect(() => {
    if (explainLineNo !== null && !livePricing.has(explainLineNo)) {
      setExplainLineNo(null);
    }
  }, [livePricing, explainLineNo]);

  // The explanation panel needs both halves: the Material row for the inputs
  // the app sent, and the stored engine result for the chain. Computed up
  // here, above the early returns, because the rail effect below is a hook
  // and hooks cannot sit after them.
  const explainLine = explainLineNo === null
    ? undefined
    : rows.find((r) => r.lineNo === explainLineNo);
  const explainPriced = explainLineNo === null
    ? undefined
    : livePricing.get(explainLineNo);
  const explainOpen = !!(explainLine && explainPriced);

  // Right-hand chrome: the explanation panel (420px) plus an EXPANDED Quote
  // Analysis panel (360px) squeezes the table both of them describe. The
  // analysis panel collapses to its 28px rail instead of unmounting — it owns
  // controls that write PricingContext (FX rate/date/risk, negotiation
  // buffer) and, in import mode, the real shipment editor whose bills and
  // bases the engine allocates across the very table being explained.
  // Unmounting would also reset its accordion sections and its two
  // uncommitted keystroke buffers.
  //
  // Collapsed ONCE, on the open transition — this effect depends on the
  // explainOpen boolean alone, so it does not re-run when the user toggles the
  // rail back open while reading an explanation. The functional updater is
  // what keeps panelCollapsed out of the dependency list; reading it from the
  // closure would put it there, and the effect would then re-collapse the rail
  // every time the user expanded it.
  //
  // autoCollapsedRail records whether WE collapsed it, so closing the
  // explanation restores only what this did. Three cases it gets right: a rail
  // the user already had collapsed stays collapsed afterwards; a rail this
  // collapsed is reopened; and a rail the user expanded by hand mid-read is
  // left alone, because onToggle clears the flag.
  const autoCollapsedRail = useRef(false);

  useEffect(() => {
    if (explainOpen) {
      setPanelCollapsed((wasCollapsed) => {
        autoCollapsedRail.current = !wasCollapsed;
        return true;
      });
    } else if (autoCollapsedRail.current) {
      autoCollapsedRail.current = false;
      setPanelCollapsed(false);
    }
  }, [explainOpen]);

  // If any of it changes after pricing — the user edits a qty or a discount
  // or an import input, adds materials, or flips the pricing mode — the
  // stored engine numbers describe inputs that no longer exist, so they get
  // dropped rather than left sitting under a "live" badge. A stale validated
  // number is still a wrong number.
  const pricingSignature = useMemo(
    () => pricingSignatureOf(rows, pricingMode),
    [rows, pricingMode],
  );
  const pricedSignatureRef = useRef("");

  useEffect(() => {
    if (liveStatus === "ok" && pricingSignature !== pricedSignatureRef.current) {
      setLivePricing(new Map());
      setLiveStatus("idle");
    }
  }, [pricingSignature, liveStatus]);

  // gp-per-quote: the GP the held engine numbers were priced at. A GP edit
  // makes them describe a margin no longer on screen, so they are dropped
  // exactly as a line edit drops them. null = priced at the stored GP.
  const pricedGpRef = useRef<number | null>(null);
  useEffect(() => { pricedGpRef.current = null; }, [id, savedId]);
  useEffect(() => {
    const pricedAt = pricedGpRef.current ?? storedGp;
    if (liveStatus === "ok" && gpPct !== pricedAt) {
      setLivePricing(new Map());
      setLiveStatus("idle");
    }
  }, [gpPct, storedGp, liveStatus]);

  // Persisting this quote to Odoo. Mirrors the live-pricing state above:
  // same shape, same never-report-what-didn't-happen discipline.
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveError, setSaveError] = useState("");
  const [savedQuoteId, setSavedQuoteId] = useState<number | undefined>(undefined);

  // THE QUOTE ROW A PO ATTACHES TO, or undefined when there is not one yet.
  //
  // Two sources and the order matters: `savedQuoteId` is the id this session's
  // Save press just came back with, and `quote.odooQuoteId` is what a reopened
  // or previously-saved quote carries. Preferring the fresher one means the PO
  // control appears the moment a save succeeds rather than after a reload.
  const poQuoteId = savedQuoteId ?? quote?.odooQuoteId;
  const [savedGrandTotal, setSavedGrandTotal] = useState<number | undefined>(undefined);
  // The reference Odoo assigned. Read back from the save response rather
  // than kept as the locally-invented quoteNo, which for a new quote was
  // never the name the record actually got.
  const [savedName, setSavedName] = useState<string | undefined>(undefined);
  // WHO FINALISED IT AND WHEN, off the save response -- set and cleared in
  // exactly the places savedName is, so the two can never disagree.
  const [savedFinaliser, setSavedFinaliser] = useState<
    { by: string | null; at: string | null; editedBy: string | null; editedAt: string | null }
    | undefined
  >(undefined);

  // IS THIS QUOTE ALREADY FINAL? Just saved from this tab, or reopened as a
  // finalised quote. Only a quote that is NOT yet final can be refused a
  // finalise -- a re-save draws no number, so the guard has nothing to protect.
  const alreadyFinal =
    savedQuoteId !== undefined || (isSavedMode && saved?.is_draft === false);

  // TASK 5 -- LINES THAT CANNOT PRICE, which the backend refuses to finalise.
  // A courtesy mirror of that refusal; see services/finaliseGuard.ts.
  const finaliseBlocks = useMemo(
    () => (alreadyFinal ? [] : finaliseBlockers(rows, pricingMode, supplierCurrency)),
    [alreadyFinal, rows, pricingMode, supplierCurrency],
  );

  // The header pill. A quote finalised FROM THIS TAB is still built from a
  // Draft-status record until it is re-read, so the press's own answer decides.
  // quote-won-lost: on a finalised quote the outcome decides the pill.
  const outcomeNow = outcomeOverride ?? quote?.outcome;
  const headerStatus =
    quote && savedQuoteId !== undefined && quote.status === "Draft"
      ? (outcomeOverride?.status === "won" ? "Won" : "Finalised")
      : quote && quote.status !== "Draft" && outcomeNow?.status ? OUTCOME_PILL[outcomeNow.status]
      : quote?.status;

  // The finaliser to show: this tab's finalise, else the reopened quote's.
  const finaliser = savedFinaliser
    ?? (isSavedMode && saved?.is_draft === false
      ? {
        by: saved.finalised_by ?? null, at: saved.finalised_at ?? null,
        editedBy: saved.final_edited_by ?? null, editedAt: saved.final_edited_at ?? null,
      }
      : undefined);

  // TASK 6 -- A FINALISED QUOTE'S TOTAL IS LOCKED. Everything that feeds it
  // (prices, quantities, discounts, landed-cost inputs, the lines, the mode,
  // the currency, the shipments) is read-only here; notes stay editable. The
  // backend refuses the same changes whatever a client sends -- this is the
  // courtesy, and the notice below says why.
  const frozen = alreadyFinal;

  // save-draft-finalise: a draft opened from the Quotes list (saved mode) is
  // edited and autosaved like one created here -- by its owner or an Admin,
  // and not while it awaits approval (canEdit already says both).
  const savedDraftEditable = isSavedMode && saved?.is_draft === true;
  // WHAT A DRAFT SAVE WOULD SEND, as one string: the lines exactly as the save
  // maps them, plus every quote-level input the save carries. Equal to
  // lastSavedSig = nothing unsaved.
  const draftSignature = useMemo(() => JSON.stringify({
    lines: rows.map((r) => materialToBackendSaveLine(r, pricingMode)),
    gp: gpPct, mode: pricingMode, currency: supplierCurrency ?? null,
    shipments, ctx,
    customer: customerOverride ?? null,
    warehouse: warehouseOverride === undefined ? "untouched" : warehouseOverride,
  }), [rows, gpPct, pricingMode, supplierCurrency, shipments, ctx, customerOverride, warehouseOverride]);

  // The backend row holding this quote's working state. Undefined until one
  // exists. Save finalises THIS row rather than creating a second one.
  const [draftId, setDraftId] = useState<number | undefined>(undefined);
  // ux-guided-deal: a draft save rewrites the quote's lines, so the OEM views
  // (which name lines by id) are read again after every successful save.
  const [draftSaves, setDraftSaves] = useState(0);
  // discard-draft: the persisted DRAFT's id -- a new one's autosave id, or the
  // reopened draft's. Undefined for a finalised quote or one never saved.
  const discardId = alreadyFinal ? undefined
    : (draftId ?? quote?.odooQuoteId ?? (isSavedMode && saved?.is_draft ? saved.quote_id : undefined));
  const doDiscard = async () => {
    if (discardId === undefined) return;
    setDiscardBusy(true);
    setDiscardError("");
    const result = await discardDraft(discardId);
    setDiscardBusy(false);
    if (!result.ok) {
      setDiscardError(result.forbidden ? "You may not discard this draft." : (result.error ?? "The draft was not discarded."));
      return;
    }
    setDiscardOpen(false);
    setDeleted(true);
  };

  // SOMEONE ELSE FINALISED THIS QUOTE WHILE THIS TAB HELD IT (BF5c-2f-iii).
  //
  // Set only by a 409 quote_finalised from a draft tick. Once true this tab
  // can never write again -- finalising is a one-way door, so the next tick
  // and every one after it get the same refusal -- so this both STOPS the
  // autosave and puts the reason on screen, instead of leaving the page
  // looking like it is still working on a document it cannot save.
  //
  // ⚠️ A LATCH, NOT A LAST-ERROR. It is never cleared by a later success,
  // because there cannot be one; only opening a different quote resets it.
  const [finalisedElsewhere, setFinalisedElsewhere] = useState(false);

  // WHY THE LAST AUTOSAVE TICK WAS REFUSED, or "".
  //
  // ⚠️ THIS CLOSES A SILENT WALL THE AUTOSAVE HAS ALWAYS HAD, and the tick's
  // own comment says so: "this path has NO error surface: the only failure it
  // reports is quoteFinalised, and every other ok: false returns in silence."
  // BF5d-2 closed one case by PRE-EMPTING it -- declining to send a request
  // the allocator would refuse -- which left every other refusal invisible.
  //
  // The reachable one that matters now is the legacy customer-name branch: a
  // draft carrying a typed `partner_name` that names an existing company is
  // refused by the backend with "A customer called X already exists -- select
  // it instead." (17.0.1.34.0). Unreported, the person keeps typing into a
  // quote that has stopped saving and is told by nothing.
  //
  // ⚠️ NOT A LATCH, unlike finalisedElsewhere, and cleared by the next
  // successful tick. These refusals are recoverable -- pick the customer the
  // message names and the following tick goes through -- so latching would
  // strand a tab that has a way back. Same reasoning as the press's stale
  // branch.
  const [draftRefusal, setDraftRefusal] = useState("");

  // THE QUOTE'S CONCURRENCY TOKEN, as this tab last saw it.
  //
  // Echoed on the next save so the backend can tell a fresh edit from a
  // stale one. Two tabs on one quote each hold their own copy of this, and
  // whichever writes first advances the stored token past the other's --
  // which is the whole mechanism: the loser's next save no longer matches
  // and is refused instead of overwriting.
  //
  // ⚠️ A REF, NOT STATE, ON PURPOSE. It is read inside the autosave effect
  // and inside the Save handler, and it must not be a dependency of either:
  // making it one would re-arm the debounce every time a save came back and
  // turn the autosave into a self-feeding loop -- exactly what BF5c-2e
  // killed. Nothing renders from it, so it owes state nothing.
  //
  // undefined means "this tab has no token to offer" -- a quote never saved
  // from here, or read from a backend that predates the column. The backend
  // allows an absent token this pass, so that is a working state and not a
  // failure. 0 is NOT that state: it is a real token held by every quote
  // written before the column existed.
  const saveTokenRef = useRef<number | undefined>(undefined);

  // ORDERING ACROSS IN-FLIGHT SAVES. One lane for BOTH save paths, because
  // "which answer is newest" is a question about them together: the autosave
  // and the Save button write the same two sinks (`draftId`, `shipments`), so
  // a stale answer from either can land on a fresh answer from the other.
  //
  // They really do overlap. The Save button is disabled while `saveStatus` is
  // "saving", so two presses cannot race -- but nothing stops a timer armed by
  // an earlier edit from firing mid-press, and on a slow link one autosave can
  // still be out when the next fires (a 15s request timeout against a 1.5s
  // debounce). See services/saveLane.ts for what the rule is and why it is
  // about responses rather than requests.
  //
  // A ref so the lane survives re-renders; created lazily so each mounted
  // quote gets its own and neither inherits the other's generation.
  const saveLaneRef = useRef<SaveLane | null>(null);
  if (saveLaneRef.current === null) saveLaneRef.current = createSaveLane();

  // THE RE-ARM. Bumped whenever the lane refuses an autosave tick, purely so
  // the debounce effect below re-runs and schedules another one.
  //
  // ⚠️ LOAD-BEARING, NOT BOOKKEEPING. The lane now defers auto ticks that
  // would overlap a write, and that is only safe because the deferred edit
  // comes back. Without this bump nothing re-runs the effect -- the edit that
  // tick would have carried would sit unsaved until some OTHER dependency
  // happened to change, which is a silently lost edit in exchange for a
  // collision that was at least loud. A refused tick is DEFERRED, never
  // dropped, and this counter is the entire difference.
  const [saveRetryTick, setSaveRetryTick] = useState(0);

  // Restore runs once per quote and must complete before autosave starts, or
  // the empty initial rows would be written over the draft we are about to
  // read back.
  const [draftRestored, setDraftRestored] = useState(false);

  // ---- SAVE DRAFT / FINALISE (save-draft-finalise, owner 2026-09-24) -------
  //
  // `lastSavedSig` is the signature of what the database last accepted for
  // this draft (or held when it was opened). Equal to the current signature
  // means nothing is unsaved: the autosave skips its tick, and leaving the
  // page does not warn. null = nothing saved yet from this screen.
  const [lastSavedSig, setLastSavedSig] = useState<string | null>(null);
  const [draftSaveState, setDraftSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [draftSaveError, setDraftSaveError] = useState("");
  const [confirmFinalise, setConfirmFinalise] = useState(false);
  // Order Management O1/O2: the OEM confirmations panel reports what it loaded,
  // so the finalise dialog can WARN (never block) about unconfirmed lines.
  const [oemView, setOemView] = useState<OemConfirmationsView | null>(null);
  // Set by the saved-mode load of a DRAFT; the baseline signature is taken on
  // the next render, once the loaded rows are in state.
  const baselinePendingRef = useRef(false);

  // A different quote's save result must never appear under this one.
  useEffect(() => {
    setSaveStatus("idle");
    setSaveError("");
    setSavedQuoteId(undefined);
    setSavedGrandTotal(undefined);
    setSavedName(undefined); setSavedFinaliser(undefined);
    setDraftId(undefined);
    setLastSavedSig(null);
    setDraftSaveState("idle");
    setDraftSaveError("");
    setConfirmFinalise(false);
    baselinePendingRef.current = false;
    // ⚠️ RESET WITH THE REST OR IT LEAKS. This latch is about ONE quote; left
    // standing it would silently disable the autosave on the next quote
    // opened in this tab, which is a lost-edits bug wearing a correct banner.
    setFinalisedElsewhere(false);
    // ⚠️ RESET WITH THE REST, same reasoning as the latch above. A token
    // belongs to ONE quote; carried into the next one it would be compared
    // against a different record's counter and refuse a save that was never
    // stale -- or, worse, happen to match and allow one that was.
    saveTokenRef.current = undefined;
    setDraftRestored(false);
  }, [id, savedId]);

  // Load a saved quote and seed the table from it.
  //
  // The engine values go straight into livePricing, so the "live" columns
  // show them exactly as they would after a recalculation — because they
  // ARE engine numbers, computed at save time and stored since. Nothing is
  // recomputed here, and no request is made beyond the single read.
  useEffect(() => {
    if (!isSavedMode) return;

    let cancelled = false;
    const quoteId = Number(savedId);

    (async () => {
      setSavedStatus("loading");
      setSavedLoadError("");
      setSavedNotFound(false);

      if (!Number.isInteger(quoteId) || quoteId <= 0) {
        if (cancelled) return;
        setSavedLoadError(`"${savedId}" is not a valid saved-quote id.`);
        setSavedStatus("error");
        return;
      }

      const result = await api.getSavedQuote(quoteId);
      if (cancelled) return;

      if (!result.ok || !result.quote) {
        setSaved(null);
        setSavedNotFound(!!result.notFound);
        setSavedLoadError(result.error ?? "Backend reported an unknown failure.");
        setSavedStatus("error");
        return;
      }

      const detail = result.quote;
      // THE TOKEN AS STORED, adopted before anything can be edited. This is
      // the PRIMARY multi-tab case -- two people open /saved-quotes/:id and
      // edit -- so without this seeding, the first save from a reopened
      // quote would carry no token, land in the backend's absent-is-allowed
      // branch, and the guard would never engage on the case it exists for.
      saveTokenRef.current = detail.save_token;
      const restored = restoreFromSavedQuote(detail, ctxRef.current);

      // THE DURABLE STORE, seeded with the reopened lines. Without it the next
      // Add Materials reads `materialsByQuote[key] ?? mockMaterials`, dedupes
      // this quote's picks against 180 mock lines, numbers a new line 181, and
      // hands back mock lineNos that handleMaterialsAdded then merges into the
      // wrong rows. The endClient goes in with them because a reopened quote is
      // not in quotesStore, and it is what the discount lookup matches on.
      //
      // Seeded from restored.materials -- the SAME array that goes on screen
      // below -- rather than re-derived, so the store and the table cannot
      // start out disagreeing about line numbers.
      api.seedReopenedQuote(
        savedQuoteStoreKey(detail.quote_id),
        restored.materials,
        detail.partner_name ?? "",
      );

      // Seed the pricing fingerprint from what was just loaded, so the
      // staleness guard does not immediately blank the live columns it was
      // handed. Anything the user edits afterwards still invalidates them,
      // which is correct: an edited line was never priced with those inputs.
      //
      // The mode comes from the RESPONSE, not from the component's
      // pricingMode, and the fingerprint is computed inside
      // restoreFromSavedQuote from the same rows it hands back. That is what
      // keeps this string equal to the one the next render computes.
      //
      // It is EMPTY when the stored numbers could not be verified, which is
      // consistent rather than a special case: nothing is being shown, so the
      // fingerprint of what is being shown is nothing.
      pricedSignatureRef.current = restored.signature;

      setSaved(detail);
      // The FX rate and negotiation buffer this quote was saved with, so the
      // panel reads the same as when it was left.
      setCtx(restored.ctx);
      // The real shipments, in the SAME batch as rows and ctx. Every other
      // piece of restored state is set together here for a reason -- a render
      // that has one half of a reopened quote and not the other is what the
      // staleness guard misreads.
      setShipments(restored.shipments);
      setLivePricing(restored.live);
      setLiveStatus(restored.verified ? "ok" : "idle");
      setMaterialsOverride(restored.materials);
      setMaterialsReloadKey((k) => k + 1);
      // Seed `rows` in the same batch rather than waiting for MaterialTable
      // to report them back. Without this the next render has liveStatus
      // "ok" but rows still empty, so pricingSignature is "" while the ref
      // holds the loaded signature — and the staleness guard immediately
      // blanks the engine numbers it was just handed.
      setRows(restored.rows);
      // save-draft-finalise: a reopened DRAFT autosaves into this row, exactly
      // like a draft created on this screen. The baseline is taken once the
      // rows above are rendered, so opening a quote writes nothing.
      if (detail.is_draft) {
        setDraftId(detail.quote_id);
        baselinePendingRef.current = true;
      }
      setSavedStatus("ok");

      // ---- Stock levels, AFTER the quote is already on screen -------------
      //
      // ⚠️ DELIBERATELY NOT IN THE BATCH ABOVE, and deliberately not awaited
      // before it. Stock is an annotation on lines that are already correct
      // and already priced, so holding the whole workspace behind a second
      // network round-trip would make a cold stock cache look like a slow
      // quote. Everything above renders first; the levels land when they land.
      //
      // ⚠️ FAIL SOFT. A failure leaves every line's stock undefined -- which
      // is exactly what it already is -- so there is nothing to undo and
      // nothing to report. Unknown is the honest resting state here.
      //
      // Safe to apply late because stock is NOT part of pricingSignatureOf:
      // merging it in cannot move the fingerprint and so cannot trip the
      // staleness guard into blanking the engine numbers just restored.
      //
      // ⚠️ LINES WITH NO productId STAY UNKNOWN. A free-text line was never
      // linked to a catalog product, so there is nothing to look up and a 0
      // would be inventing a count for something the warehouse has no record
      // of. withStockLevels leaves them untouched.
      const stockIds = restored.materials
        .map((m) => m.productId)
        .filter((pid): pid is number => pid !== undefined);
      if (stockIds.length === 0) return;

      // ?locations=1 here, unlike the picker: a reopened line has a single
      // warehouse slot to fill and singleWarehouseName only fills it when the
      // breakdown holds exactly one named location, so the tooltip can name a
      // place without ever attributing a multi-warehouse total to one of them.
      const levels = await getStock(stockIds, { locations: true });
      if (cancelled || !levels.ok || !levels.stock) return;

      setMaterialsOverride((prev) =>
        prev ? withStockLevels(prev, levels.stock, levels.locations) : prev,
      );
      setRows((prev) => withStockLevels(prev, levels.stock, levels.locations));
      // The table is seeded from `initial` on mount, so a remount is what
      // makes the merged levels visible -- the same mechanism the load above
      // and Add Materials both already use.
      setMaterialsReloadKey((k) => k + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [isSavedMode, savedId, approvalReload]);

  // ---- draft: restore, then autosave ------------------------------------
  //
  // The working set is persisted through the SAME endpoint a real quote uses,
  // so it is priced by the engine and there is no second write path. What
  // makes it a draft is one flag, which also keeps it out of the quote list
  // and out of the numbering until Save is pressed.

  // RESTORE. Runs once per quote, before autosave is allowed to write.
  useEffect(() => {
    // Saved mode already loads from the backend; a read-only user has no
    // working state to keep.
    if (isSavedMode || !canEdit || !quote) {
      setDraftRestored(true);
      return;
    }

    let cancelled = false;
    (async () => {
      const found = await findDraftFor(quote.quoteNo);
      if (cancelled) return;

      if (found !== undefined) {
        const result = await api.getSavedQuote(found);
        if (cancelled) return;
        if (result.ok && result.quote) {
          setDraftId(found);
          // The restored draft's token, on the same terms as saved mode
          // above: this tab is now holding that row, so its very first
          // autosave tick must echo the token it was restored at rather
          // than claiming it cannot say.
          saveTokenRef.current = result.quote.save_token;
          // Seed the table and the engine numbers together, in one batch --
          // the staleness guard compares a fingerprint of rows against what
          // was priced, so setting them a render apart makes it drop the
          // numbers it was just handed.
          const restored = restoreFromSavedQuote(result.quote, ctxRef.current);
          // THE DURABLE STORE, same reason and same call as the saved-mode
          // reopen above. This path is NOT saved mode -- it restores a draft
          // onto /quotes/:id -- so it keys on the quoteNo, which is what
          // storeKey already is here. It still needs seeding: the 25 original
          // mock quotes have no materialsByQuote entry at all, so a pick added
          // after a draft restore fell through to mockMaterials exactly as a
          // reopened quote's did.
          api.seedReopenedQuote(storeKey, restored.materials, quote.endClient);
          // Go through materialsOverride + a remount, the SAME mechanism Add
          // Materials uses. Setting `rows` alone is NOT enough: MaterialTable
          // seeds its own copy from `initial` once and never re-seeds, so the
          // restored lines would sit in this component while the table went
          // on showing the mock ones. That is exactly what happened the first
          // time -- the draft saved correctly and the reload showed qty 4.
          setMaterialsOverride(restored.materials);
          setMaterialsReloadKey((k) => k + 1);
          setRows(restored.rows);
          setCtx(restored.ctx);
          setShipments(restored.shipments);
          setLivePricing(restored.live);
          setLiveStatus(restored.verified ? "ok" : "idle");
          // THE FINGERPRINT. Its absence here was the bug: this path handed
          // the table engine numbers and left the ref empty, so the staleness
          // guard compared "" against the restored rows, disagreed, and blanked
          // them on the very next render. A restored draft looked unpriced
          // until someone pressed Update prices.
          pricedSignatureRef.current = restored.signature;
          // THE STORE, NOT JUST THE OVERRIDE. Setting only the override left
          // the screen reading "import" while the stored quote still read
          // domestic -- and both save paths derive the payload's
          // `pricing_mode` from the STORED quote, so every autosave declared
          // domestic while the screen showed import. Harmless until shipments
          // existed; after them the backend refused the whole save. Writing
          // both is what changePricingMode already does for a user click.
          if (result.quote.pricing_mode) {
            setPricingModeOverride(result.quote.pricing_mode);
            api.setQuotePricingMode(id, result.quote.pricing_mode);
          }
          // Same divergence, same fix: the currency gates whether a shipment
          // may be sent at all, and the payload reads it off the stored quote.
          if (result.quote.supplier_currency) {
            setSupplierCurrencyOverride(result.quote.supplier_currency);
            api.setQuoteSupplierCurrency(id, result.quote.supplier_currency);
          }
        }
      }
      setDraftRestored(true);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, savedId, isSavedMode, canEdit]);

  // ---- a working reference this tab does not hold -------------------------
  //
  // ⚠️ THE SCREEN THIS FEEDS USED TO SAY "Quote not found -- No quote exists
  // with id Q-...", AND THAT WAS FALSE. A new quote lives in quotesStore,
  // which reseeds from the mocks on every page load, so a reload, a restored
  // tab or a pasted link lands here with no quote in memory -- while the
  // autosave has already written the work to Odoo as a draft under this very
  // reference, where the quote list shows it.
  //
  // So the reference is looked up among THIS user's drafts (server-scoped,
  // see resolveWorkingReference). Exactly one match -> the saved draft, via
  // the same read-only route the quote list opens it on. Anything else is
  // said as it is, and nothing is guessed.
  //
  // ⚠️ READ-ONLY. One GET. The restore effect above has already returned on
  // `!quote` and the autosave below returns on it too, so nothing on this
  // path can write.
  //
  // null is "still looking": the screen claims nothing until there is an
  // answer, because both "saved" and "not saved" would be guesses.
  const [refLookup, setRefLookup] = useState<WorkingReferenceLookup | null>(null);
  const [refLookupTick, setRefLookupTick] = useState(0);
  const refNotInTab = !isSavedMode && !quote;
  useEffect(() => {
    if (!refNotInTab) return;
    // /quotes/:id always has a segment in the router; an empty one names no
    // reference, so there is nothing to look up.
    if (!id) {
      setRefLookup({ status: "none" });
      return;
    }
    let cancelled = false;
    setRefLookup(null);
    resolveWorkingReference(id).then((result) => {
      if (!cancelled) setRefLookup(result);
    });
    return () => { cancelled = true; };
  }, [refNotInTab, id, refLookupTick]);

  // AUTOSAVE, debounced. Every edit that changes a line or the mode lands
  // here; the debounce is what stops a keystroke becoming a request.
  useEffect(() => {
    // save-draft-finalise: a DRAFT reopened from the Quotes list autosaves
    // too (it used to be skipped, so every edit there was lost unless the
    // quote was finalised). A finalised quote never does.
    if (!draftRestored || (isSavedMode && !savedDraftEditable) || !canEdit || !quote) return;
    // Never a create from a reopened quote: it already has its row.
    if (isSavedMode && draftId === undefined) return;
    if (rows.length === 0) return;
    // A reopened draft: nothing changed since the database last accepted it
    // (or since it was opened -- opening a quote writes nothing). A draft
    // created on this screen keeps its autosave exactly as it was.
    if (isSavedMode && lastSavedSig !== null && draftSignature === lastSavedSig) return;
    const sigAtSchedule = draftSignature;
    // C1 -- ⚠️ THIS QUOTE'S LINES ARE THE SHARED EXAMPLE SET. DO NOT WRITE
    // THEM.
    //
    // `rows.length === 0` was the only content gate here, and 180 is not 0:
    // every one of the 25 quotes in mocks/quotes.ts is handed the same 180
    // example lines by getMaterials' `?? mockMaterials` fallthrough, so
    // opening one and touching anything the autosave watches wrote them into
    // the production database 1.5s later. Measured on live 2026-09-07: 4335
    // of 4346 stored lines came from this path.
    //
    // ⚠️ CHECKED HERE, OUTSIDE THE TIMER, NOT BESIDE THE TWO REFUSALS INSIDE
    // IT. Those two describe a quote that is real but not yet saveable, and
    // they re-arm; this one is a permanent property of which quote is open
    // and nothing about it can change while the timer runs. Arming a debounce
    // to discover that would be scheduling work that can only ever be thrown
    // away.
    //
    // The banner is derived at render time from the same predicate, so the
    // refusal is already on screen before anyone reaches for Save -- unlike
    // the two inside the timer, this one has no moment of user action to
    // report itself at.
    if (exampleDataIssue) return;
    // Once finalised there is no draft to keep -- the quote itself is saved.
    if (savedQuoteId !== undefined) return;
    // ⚠️ FINALISED BY SOMEONE ELSE: STOP, DO NOT DEFER. Every other reason
    // this effect declines to send is temporary and the tick comes back --
    // that is what `saveRetryTick` exists for. This one is permanent, so
    // re-arming would produce an unbounded run of requests that are all
    // refused, and there is nothing left this tab could ever save.
    if (finalisedElsewhere) return;

    const timer = setTimeout(async () => {
      // BF5d-2 -- DO NOT SEND A REQUEST THE ALLOCATOR WILL REFUSE.
      //
      // A By-Weight or By-Volume shipment whose assigned lines all carry a zero
      // dimension makes the engine raise, and the controller rolls the whole
      // transaction back -- so this tick would take every line edit in it down
      // too, every 1.5s, and this path has NO error surface: the only failure
      // it reports is `quoteFinalised`, and every other `ok: false` returns in
      // silence. That silent wall is what this slice exists to close.
      //
      // ⚠️ EVALUATED AGAINST WHAT IS ABOUT TO BE SENT -- `rows` from this
      // closure and `shipmentsRef.current` -- the same two values handed to
      // saveDraft below, not the render-time memo that drives the banner. Guard
      // the quote you SEND, not one that looked like it a frame ago.
      //
      // ⚠️ NO `saveRetryTick` BUMP, DELIBERATELY, and it is the opposite of the
      // lane's deferred-not-dropped rule. A refused ticket is a collision that
      // clears on its own, so it must re-arm. This is a data condition that
      // will not clear until a person changes something -- and every change
      // that could fix it (a basis, an assignment, a line) already moves
      // `shipments` or `rows`, which are dependencies of this effect. Bumping
      // as well would spin the debounce forever against a condition no tick can
      // resolve.
      //
      // Nothing is set here: the banner is derived from the same predicate at
      // render time, so it is already on screen. A flag written from inside a
      // timer would be a second copy of a fact that is computed anyway, and it
      // would then need clearing.
      // Same two refusals as the manual press, in the same order. Nothing is
      // set here: both banners are derived from these predicates at render
      // time, so they are already on screen. A flag written from inside a
      // timer would be a second copy of a fact that is computed anyway.
      if (unpricedProblems(rows).length > 0) {
        return;
      }
      if (freightBasisProblems(shipmentsRef.current, rows, quoteForSave?.pricingMode).length > 0) {
        return;
      }

      // A null ticket means this tick must not be sent: either a create is
      // already in flight and this one would make a SECOND quote, or a write
      // is out / a manual press is waiting and this one would collide with it.
      //
      // ⚠️ DEFERRED, NOT DROPPED. What this tick would have saved is still in
      // `rows`, so re-arming the debounce is enough to carry it -- but the
      // re-arm has to be explicit. The create case used to get one for free
      // (the create's response sets `draftId`, which is a dependency), and
      // the new collision case gets nothing for free at all: an ordinary
      // update refused mid-write changes no dependency, so without the bump
      // the edit would wait for the next unrelated change to the quote.
      const ticket = saveLaneRef.current!.begin("auto", draftId === undefined);
      if (!ticket) {
        setSaveRetryTick((t) => t + 1);
        return;
      }

      // quoteForSave, not `quote` -- see its definition. The autosave is the
      // path that runs unattended, so a stale mode here is the one nobody
      // would notice. The shipments ride along on the same object's mode and
      // currency, which is what makes the shipment half of the drop fix
      // itself with no extra code here.
      let result;
      try {
        result = await saveDraft(
          quoteForSave, rows, VARIANCE_PCT, gpPct, draftId, ctxRef.current,
          shipmentsRef.current, saveTokenRef.current,
        );
      } finally {
        ticket.done();
      }

      // SUPERSEDED: a newer save began while this one was out, so this answer
      // describes a quote state that has already been replaced. Applying it
      // rolls the screen backwards -- most visibly by handing `shipments` a
      // list that predates a shipment added since, which MaterialTable then
      // reads as a deletion and clears the lines on it. Dropped whole rather
      // than merged: nothing distinguishes the still-true parts of a stale
      // answer from the rest.
      if (ticket.superseded()) return;

      // THE ONE FAILURE THAT MEANS STOP RATHER THAN TRY AGAIN. Another tab
      // finalised this quote, so the backend refused this draft-ward write
      // and will refuse every later one identically. Latching here is what
      // ends the cycle; the banner is what makes the refusal visible, which
      // it was not before -- the quote stayed safe and the person kept typing
      // into a document that could no longer be saved.
      //
      // Checked BEFORE `result.ok`, and read off the flag rather than the
      // message: backendRequest owns the wire string, and matching display
      // prose here is how a reworded error silently switches a guard off.
      //
      // ⚠️ DELIBERATELY AFTER THE SUPERSESSION CHECK ABOVE, WHICH IS WHAT
      // STOPS THIS TAB ACCUSING ITSELF. A 409 says only "this quote is final",
      // not "somebody else made it so". The reachable self-inflicted case is
      // IDLE_WAIT_CAP_MS: if the press gives up waiting and goes while an auto
      // tick is still out, OUR OWN finalise can be what refuses that tick --
      // and latching then would raise "finalised elsewhere" next to this tab's
      // own "Saved to Odoo". The press takes a later generation, so that tick
      // is superseded and returns above; every tick that reaches this line was
      // the newest save when it answered.
      if (result.quoteFinalised) {
        setFinalisedElsewhere(true);
        return;
      }

      // ⚠️ EVERY OTHER REFUSAL, MADE VISIBLE. Recorded here rather than
      // branched on: this screen does not need to know WHICH refusal it was,
      // only that the work on it is not saved and why the backend says so.
      //
      // ⚠️ SUPPRESSED WHEN THERE WERE NO ROWS TO SEND, and that is a fact
      // about the inputs rather than a match on the message. saveDraft
      // declines a lineless payload without calling the server at all, so
      // there is no server refusal to report -- and an empty quote screen
      // sits at zero rows for as long as somebody leaves it open, which would
      // otherwise put a permanent "not saved" on a page with nothing on it.
      setDraftRefusal(result.ok || rows.length === 0 ? "" : (result.error ?? ""));

      // Record the id so the next autosave UPDATES this row rather than
      // adding another, and so Save can finalise it in place.
      if (result.ok && result.quoteId !== undefined) setDraftId(result.quoteId);
      if (result.ok) { setLastSavedSig(sigAtSchedule); setDraftSaves((n) => n + 1); }
      // ADOPT THE TOKEN THIS TICK LEFT BEHIND, so the next one echoes the
      // current value rather than the one this tick just superseded --
      // without it a tab would refuse its own second save.
      //
      // ⚠️ DELIBERATELY BELOW `if (ticket.superseded()) return;` ABOVE, on
      // exactly the terms adoptSavedShipments is. A superseded answer
      // describes a quote state that has already been replaced, so its token
      // is already behind the stored one -- storing it here would hand the
      // NEXT save a token guaranteed to mismatch, and this tab would refuse
      // itself with no other tab involved. That is the same class of bug as
      // adopting a stale shipment list, reached the same way.
      if (result.ok && result.saveToken !== undefined) {
        saveTokenRef.current = result.saveToken;
      }
      // Same reasoning one level down: adopt the ids the backend issued so the
      // NEXT tick updates these shipments instead of creating fresh copies and
      // deleting the originals. At 1.5s intervals that churn would be
      // continuous, and it would re-point any line assigned to one.
      //
      // `rows` is the THIRD argument for the key half: it is the very array
      // just sent, so it lines up positionally with result.lines and lets
      // adopt prove which panel key became which id -- and therefore keep the
      // key a line is still holding. Without it a shipment added here is
      // renamed within 1.5s and the table clears the line that named it.
      if (result.ok) {
        const nextShipments = adoptSavedShipments(result, shipmentsRef.current, rows);
      // THE SHARE THE ALLOCATOR JUST DECIDED, AND THE PRICES IT PRODUCED.
      //
      // Derived BEFORE setShipments so the adopted list -- which carries the
      // PANEL's keys, not the backend's ship-{id} form -- is what the
      // correspondence check fingerprints against. Reversing this makes every
      // newly-created shipment fail that check and adopt nothing.
      //
      // ⚠️ THE REF ADVANCES SYNCHRONOUSLY, BEFORE setRows. `freightPct` is a
      // fingerprint term, so the row write moves pricingSignature; a ref
      // assignment is not batched, so setting it first means the staleness
      // guard compares against the NEW value on whichever render lands first.
      // Set it after, or in a later tick, and there is one frame where the
      // guard fires and blanks the live columns of a quote that just saved
      // successfully -- and it does not heal, because it also sets liveStatus
      // to idle and nothing re-prices.
      //
      // adoptSavedPricing returns rows, prices and signature as one answer or
      // refuses outright, so the three can never be applied by halves.
        const adopted = adoptSavedPricing(
          result, rows, rowsRef.current, nextShipments, pricingMode,
        );
        setShipments(nextShipments);
        if (adopted.adopted) {
          pricedSignatureRef.current = adopted.signature;
          applyAdoptedPricing(adopted);
        }
      }
    }, 1500);

    return () => clearTimeout(timer);
    // ctx is in the list so moving an FX slider or editing a shipment is
    // itself a change worth saving. Without it the panel settings would only
    // reach the backend the next time a LINE changed, so adjusting the
    // exchange rate and reloading would lose it -- the exact gap this change
    // set out to close.
    //
    // `shipments` is here for the same reason as `ctx`: adding or deleting one
    // is itself a change worth saving, and without it a new shipment would
    // only reach the backend the next time a LINE changed. `supplierCurrency`
    // is here because it GATES what gets sent -- a stale closure would keep
    // withholding shipments after one was set.
    //
    // `saveRetryTick` is here for a different reason from every other entry:
    // it describes nothing about the quote. It exists so a tick the lane
    // REFUSED re-arms this timer, and it is the only dependency that moves
    // when an autosave is deferred -- see its declaration.
    //
    // `exampleDataIssue` is here because it can STOP being set while the
    // screen is open -- a restored draft or a first pick gives this quote a
    // list of its own -- and the autosave has to start working at that point
    // rather than staying latched off until something else happens to move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, pricingMode, gpPct, ctx, shipments, supplierCurrency, draftRestored, draftId, savedQuoteId,
      saveRetryTick, finalisedElsewhere, exampleDataIssue, savedDraftEditable, lastSavedSig, draftSignature]);

  // save-draft-finalise: the baseline of a draft reopened from the list --
  // what the database holds -- once its rows are on screen.
  useEffect(() => {
    if (baselinePendingRef.current && savedStatus === "ok") {
      baselinePendingRef.current = false;
      setLastSavedSig(draftSignature);
    }
  }, [savedStatus, draftSignature]);

  // UNSAVED CHANGES: a draft whose current state is not what the database
  // last accepted. Covers the autosave's 1.5 s window, a refused save and a
  // line that cannot be saved yet (no price).
  const hasUnsavedChanges = !alreadyFinal && canEdit && rows.length > 0
    && !finalisedElsewhere && draftSignature !== lastSavedSig
    && (isSavedMode ? savedDraftEditable : true);

  // session-expired-redirect: a 401 while this draft holds unsaved changes
  // warns before the sign-in screen replaces it.
  const hasUnsavedRef = useRef(false);
  hasUnsavedRef.current = hasUnsavedChanges;
  useEffect(() => registerUnsavedWork(() => hasUnsavedRef.current), []);

  // "You have unsaved changes" on closing or reloading the tab (the browser
  // shows its own wording for that one) and on following an in-app link.
  // The router here is a BrowserRouter, which has no navigation blocker, so
  // links are checked in the capture phase: a cancelled confirm prevents the
  // click before the router's Link sees it.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = UNSAVED_CHANGES_TEXT;
      return UNSAVED_CHANGES_TEXT;
    };
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;
      if (!window.confirm(`${UNSAVED_CHANGES_TEXT} Leave this page anyway?`)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [hasUnsavedChanges]);

  // gst-split: THE DOCUMENT'S TAX, read from the server when the preview
  // opens -- after any save in flight has landed, so it is worked out on
  // what is stored. Never computed here. Until it arrives (or if the read
  // fails) the document shows "…" rather than a tax figure.
  const [pdfTax, setPdfTax] = useState<QuoteTax | undefined>(undefined);
  const [pdfTaxPending, setPdfTaxPending] = useState(false);
  useEffect(() => { setPdfTax(undefined); }, [id, savedId]);
  useEffect(() => {
    const target = poQuoteId;
    if (!pdfPreviewOpen || target === undefined) return;
    let cancelled = false;
    setPdfTaxPending(true);
    setPdfTax(undefined);
    (async () => {
      await saveLaneRef.current?.whenIdle();
      const result = await getQuoteTax(target);
      if (cancelled) return;
      if (result.ok && result.tax) {
        setPdfTax(result.tax);
        setPdfTaxPending(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfPreviewOpen, poQuoteId]);

  // warehouse-master: once a CHANGED dispatch warehouse has been saved, read the
  // server's tax block again so the GST note on screen follows it. Computed on
  // the server, never here.
  useEffect(() => {
    const target = poQuoteId ?? draftId;
    if (warehouseOverride === undefined || target === undefined || lastSavedSig !== draftSignature) return;
    let cancelled = false;
    (async () => {
      const result = await getQuoteTax(target);
      if (!cancelled && result.ok && result.tax) setPdfTax(result.tax);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedSig, warehouseOverride]);

  // Load the issuing company's identity for the document. One source for the
  // app and the PDF: whatever an Admin typed into Settings is what prints.
  useEffect(() => {
    // Also read when the finalise confirmation opens: it needs the validity
    // days to say whether an OEM price caps Valid-until (validity-cap-from-oem).
    if ((!pdfPreviewOpen && !confirmFinalise) || companySettings) return;
    let cancelled = false;
    (async () => {
      const result = await api.getCompanySettingsFromBackend();
      if (!cancelled && result.ok && result.settings) setCompanySettings(result.settings);
    })();
    return () => { cancelled = true; };
  }, [pdfPreviewOpen, confirmFinalise, companySettings]);

  const saveQuote = async () => {
    if (!quote) return;

    // BF5d-2 -- BLOCK THE PRESS THE ALLOCATOR WOULD REFUSE, and say so.
    //
    // ⚠️ BEFORE `setSaveStatus("saving")` AND BEFORE THE LANE. Announcing a
    // save, taking a place in the order and then declining would leave the
    // button flickering through "Saving..." for a request that was never sent,
    // and would park a manual waiter in `whenIdle` for nothing.
    //
    // ⚠️ THE SAME TWO VALUES saveQuoteToBackend IS GIVEN BELOW -- `rows` from
    // this closure and `shipmentsRef.current` -- not the render-time memo that
    // drives the banner. Guard the quote you SEND.
    //
    // REPORTED THROUGH BOTH BANNERS, deliberately, because they answer
    // different questions. FreightBasisBanner is already on screen (it is
    // derived, not raised here) and describes the state of the QUOTE; this
    // error describes THIS PRESS, which really did happen and is owed an
    // outcome. Silently doing nothing on a press is the one option that is
    // worse than either.
    // ⚠️ A LINE WITH NO PRICE BLOCKS THE SAVE, and is checked FIRST because
    // it is the more fundamental problem: a freight basis decides how a cost
    // is split, but an unpriced line has no cost to split at all.
    //
    // Blocked rather than saved-with-a-zero. The backend would accept
    // unit_price 0 and the engine would return a perfectly valid zero, so the
    // quote would PERSIST with a Rs 0 line that reads as deliberately priced
    // -- and reopening it shows engine numbers, because they are engine
    // numbers. A save that did not happen is recoverable; a stored quote that
    // silently prices a part at nothing is not.
    //
    // Guard the rows being SENT, from this closure, for the same reason the
    // freight check below does.
    // C1 -- ⚠️ THE PRESS IS THE SAME DEFECT WITH ONE EXTRA CLICK.
    //
    // The autosave gate above stops the unattended write; without this, Save
    // still wrote the same 180 example lines the moment anyone pressed it.
    // Half-fixing it would leave exactly the hole C1 exists to close.
    //
    // ⚠️ CHECKED FIRST, BEFORE THE UNPRICED AND FREIGHT REFUSALS BELOW,
    // because it is the more fundamental answer: those two say "this quote is
    // not ready to save", and this one says "this is not your quote". Telling
    // someone to type a List Price into a demo line would be advice that
    // leads nowhere.
    //
    // ⚠️ A CALL, NOT A CONDITION. The predicate lives in
    // services/mockQuoteGuard.ts with the message; this seam asks it. That is
    // what keeps the token, lane, shipment and supersession machinery in this
    // function free of a test about which quote is open.
    //
    // Derived here from the store rather than read off the render-time memo,
    // for the same reason the two below re-derive: guard the quote you SEND.
    // ⚠️ C1a -- THIS IS THE SEAM THAT FAILED ON LIVE. The press was allowed on
    // an example quote whose pre-C1 draft had restored, and it did not merely
    // save: is_draft: false FINALISES, so ir.sequence issued a real number and
    // 180 generated lines landed on /saved-quotes as a numbered quote.
    const exampleBlocking = exampleDataProblem(
      quote.quoteNo,
      {
        isSavedMode,
        hasOwnMaterialStore: api.hasOwnMaterialStore(storeKey),
        isExampleQuoteNo: api.isExampleQuoteNo(quote.quoteNo),
      },
      rows,
    );
    if (exampleBlocking) {
      setSavedQuoteId(undefined);
      setSavedGrandTotal(undefined);
      setSavedName(undefined); setSavedFinaliser(undefined);
      setSaveError(exampleDataMessage(exampleBlocking));
      setSaveStatus("error");
      return;
    }

    const unpricedBlocking = unpricedProblems(rows);
    if (unpricedBlocking.length > 0) {
      setSavedQuoteId(undefined);
      setSavedGrandTotal(undefined);
      setSavedName(undefined); setSavedFinaliser(undefined);
      setSaveError(unpricedMessage(unpricedBlocking));
      setSaveStatus("error");
      return;
    }

    const blocking = freightBasisProblems(
      shipmentsRef.current, rows, quoteForSave?.pricingMode,
    );
    if (blocking.length > 0) {
      setSavedQuoteId(undefined);
      setSavedGrandTotal(undefined);
      setSavedName(undefined); setSavedFinaliser(undefined);
      setSaveError(freightBasisMessage(blocking));
      setSaveStatus("error");
      return;
    }

    setSaveStatus("saving");
    setSaveError("");

    // THE SAME LANE THE AUTOSAVE USES. This press and an autosave timer can be
    // out together and they write the same sinks.
    //
    // ⚠️ WAIT FOR THE IN-FLIGHT AUTOSAVE, THEN GO LAST. Overlapping the two
    // writes is what Postgres refuses outright ("could not serialize access
    // due to concurrent update"), and the failure lands on THIS press for no
    // reason the user can see. Worse and quieter: a draft tick sends
    // `is_draft: true`, so an autosave committing after this press finalises
    // the quote turns a numbered quote back into a draft. The generation
    // guard drops that stale ANSWER, but the WRITE already happened -- which
    // is why the ordering has to be about requests, and why it has to be the
    // press that lands last.
    //
    // `saveStatus` is set to "saving" BEFORE the wait, above, so the button
    // acknowledges the press immediately instead of looking dead while it
    // queues. The wait itself is bounded -- see IDLE_WAIT_CAP_MS.
    await saveLaneRef.current!.whenIdle();

    // AFTER the wait, deliberately: the generation this ticket takes is what
    // makes this press the newest save, and taking it before waiting would
    // hand that title to the autosave it is queueing behind.
    //
    // A press is NEVER refused, unlike an autosave tick. It has already waited
    // its turn, the button is disabled while one is saving, and refusing the
    // user's explicit Save on top of that would strand it with nothing left to
    // resolve it.
    const ticket = saveLaneRef.current!.begin("manual", false);
    // draftId turns this into "finalise that row": same record, and the
    // sequence issues its number on the transition.
    let result;
    try {
      result = await api.saveQuoteToBackend(
        quoteForSave, rows, VARIANCE_PCT, gpPct, draftId, ctx, shipmentsRef.current,
        saveTokenRef.current,
      );
    } finally {
      ticket!.done();
    }

    // ⚠️ THE SHARED SINKS ARE GATED, THIS BUTTON'S OWN REPORT IS NOT, and the
    // asymmetry is deliberate rather than an oversight.
    //
    // `draftId` and `shipments` describe the quote, so the newest answer owns
    // them and a superseded one must not touch them -- that is the guard below.
    // saveStatus/saveError/savedQuoteId describe THIS BUTTON PRESS: the record
    // really was written, and the person who pressed it is owed the outcome.
    // Suppressing those on supersession would strand the button on "Saving…"
    // forever, since nothing else ever resolves it -- a new defect introduced
    // by the fix for an old one. The reachable case is ordinary: edit, then
    // press Save at 1.4s, and the timer armed by the edit fires at 1.5s while
    // the press is still out.
    const superseded = ticket!.superseded();

    if (!result.ok || result.quoteId === undefined) {
      // No id means no proof a record exists. Never show "Saved".
      setSavedQuoteId(undefined);
      setSavedGrandTotal(undefined);
      setSavedName(undefined); setSavedFinaliser(undefined);
      // A STALE PRESS SAYS SO IN WORDS, rather than surfacing the wire
      // string. THE PRESS PATH ALREADY REPORTS ITS OWN FAILURES -- unlike
      // the autosave, which returns silently on anything but a finalised
      // refusal -- so this refusal was never going to be swallowed here;
      // what it lacked was prose. "quote_stale" in the banner tells the
      // person nothing they can act on.
      //
      // Read off the FLAG, never the message: backendRequest owns the wire
      // string, and matching display prose here is how a reworded error
      // silently switches a branch off.
      //
      // Deliberately NOT a latch and NOT a disabled button, unlike
      // finalisedElsewhere. Stale is recoverable -- reopening the quote gets
      // a current token and editing continues -- so treating it as a one-way
      // door would strand a tab that has a way back. The recovery UX is
      // 2f-iv-b; this slice only has to make the refusal visible and
      // truthful.
      // margin-approval: below the limit. Not an error banner -- the
      // approval panel says it, with the backend's figures and the action.
      if (result.approvalRequired) {
        setApprovalRefusal(result.approvalRequired);
        setSaveError("");
        setSaveStatus("idle");
        return;
      }
      setSaveError(
        result.quoteStale
          ? "Not saved — this quote was changed elsewhere since you opened it. "
            + "Reopen it to get the latest version before saving again."
          : result.error ?? "Backend reported an unknown failure.",
      );
      setSaveStatus("error");
      return;
    }

    setApprovalRefusal(undefined);
    setSavedQuoteId(result.quoteId);
    setSavedGrandTotal(result.grandTotal);
    if (result.customerSnapshot) setIssuedSnapshot(result.customerSnapshot);
    // won-at-finalise: a quote finalised while holding a PO comes back Won.
    if (result.outcome?.status) setOutcomeOverride(result.outcome);
    setSavedName(result.name);
    setSavedFinaliser({
      by: result.finalisedBy ?? null, at: result.finalisedAt ?? null,
      editedBy: result.finalEditedBy ?? null, editedAt: result.finalEditedAt ?? null,
    });
    // The shared sinks stop here when a newer save has already answered.
    if (superseded) { setSaveStatus("saved"); return; }
    // The token is a SHARED SINK, so it sits below that gate with the others:
    // a superseded press describes a state a later save has already replaced,
    // and that later save stores its own token. Adopting here instead would
    // put a value behind the stored one into the ref and make this tab refuse
    // its own next save.
    if (result.saveToken !== undefined) saveTokenRef.current = result.saveToken;
    // Adopt the ids the backend issued. Without this a second press of Save
    // sends the same id-less shipments again, which CREATES duplicates and
    // then deletes the originals the payload no longer names -- the ids churn
    // on every save, and in the next slice that would re-point the lines
    // attached to them.
    //
    // `rows` as sent, for the same reason as the autosave above: it matches
    // result.lines positionally, which is what lets adopt keep the key a line
    // is still holding rather than renaming it out from under the row.
    const nextShipments = adoptSavedShipments(result, shipmentsRef.current, rows);
    // THE SHARE THE ALLOCATOR JUST DECIDED, AND THE PRICES IT PRODUCED.
    //
    // Derived BEFORE setShipments so the adopted list -- which carries the
    // PANEL's keys, not the backend's ship-{id} form -- is what the
    // correspondence check fingerprints against. Reversing this makes every
    // newly-created shipment fail that check and adopt nothing.
    //
    // ⚠️ THE REF ADVANCES SYNCHRONOUSLY, BEFORE setRows. `freightPct` is a
    // fingerprint term, so the row write moves pricingSignature; a ref
    // assignment is not batched, so setting it first means the staleness
    // guard compares against the NEW value on whichever render lands first.
    // Set it after, or in a later tick, and there is one frame where the
    // guard fires and blanks the live columns of a quote that just saved
    // successfully -- and it does not heal, because it also sets liveStatus
    // to idle and nothing re-prices.
    //
    // adoptSavedPricing returns rows, prices and signature as one answer or
    // refuses outright, so the three can never be applied by halves.
    const adopted = adoptSavedPricing(
      result, rows, rowsRef.current, nextShipments, pricingMode,
    );
    setShipments(nextShipments);
    if (adopted.adopted) {
      pricedSignatureRef.current = adopted.signature;
      applyAdoptedPricing(adopted);
    }
    // The draft row IS the saved quote now, so there is no draft left to
    // autosave over it.
    setDraftId(undefined);
    setSaveStatus("saved");
  };

  const runBackendPricing = async () => {
    setLiveStatus("loading");
    setLiveError("");

    // ⚠️ ONLY LINES THAT HAVE A PRICE ARE SENT. A line picked from the real
    // catalog has none until someone types one, and there is no safe way to
    // include it: sending 0 gets a COMPLETE, VALID engine result of zero back
    // (Rs 0.00 beside a confident 28.0% GP, under the green `live` badge),
    // and sending undefined becomes null on the wire, which the bridge
    // rejects in a way that aborts the WHOLE batch. Skipping it leaves its
    // own live columns at "—" -- the honest reason, since it genuinely has no
    // engine result -- and prices every other line normally.
    // See services/unpricedGuard.ts.
    const toPrice = pricableLines(rows, pricingMode);

    // ⚠️ BUILT FROM WHAT IS SENT, NOT FROM `rows`. The endpoint answers
    // positionally, so if this were `rows.map(...)` while the payload was
    // filtered, every result after the first skipped line would land on the
    // wrong row -- silently, and carrying real engine numbers, which is the
    // worst possible way to be wrong. The two must be derived from one array.
    const ordered = toPrice.map((r) => r.lineNo);

    // Nothing to ask about. Not an error and not a failed call: the columns
    // simply stay blank, which is what they already show for an unpriced
    // line, and the banner explains why.
    if (toPrice.length === 0) {
      setLivePricing(new Map());
      setLiveStatus("idle");
      return;
    }

    const signatureAtCall = pricingSignature;
    const gpAtCall = gpPct;
    const result = await api.priceLinesFromBackend(
      // Passed by value rather than bare `map(materialToBackendLine)`, which
      // would pass the row index as the second argument.
      // The currency goes to the MAPPER as well as the request: it decides
      // whether an unset rate is left out for the backend to resolve, and
      // the request is what lets it resolve one. Passing it to only one of
      // the two would either omit a rate nothing can supply, or keep
      // sending a 0 that suppresses the lookup.
      toPrice.map((r) => materialToBackendLine(r, pricingMode, supplierCurrency)),
      VARIANCE_PCT,
      gpPct,
      pricingMode,
      // The effective currency, same value the table and the save
      // paths read. The backend resolves each unrated line's FX from
      // it, which is what stops an import line pricing at zero.
      supplierCurrency,
    );

    if (!result.ok || !result.lines) {
      // Drop every previous result. Live columns go blank rather than
      // falling back to placeholder math — a stale or estimated number
      // sitting under a "live" badge is the failure this design prevents.
      setLivePricing(new Map());
      setLiveError(result.error ?? "Backend reported an unknown failure.");
      setLiveStatus("error");
      return;
    }

    const byLine = new Map<number, LivePricedLine>();
    result.lines.forEach((line, i) => {
      const lineNo = ordered[i];
      if (lineNo !== undefined) byLine.set(lineNo, line);
    });
    pricedSignatureRef.current = signatureAtCall;
    pricedGpRef.current = gpAtCall;
    setLivePricing(byLine);
    setLiveStatus("ok");
  };

  // Add Materials appends/merges onto `rows` (the live, possibly-edited
  // table state — see onRowsChange below) rather than reloading from
  // initialMaterials, so the user's in-progress cell edits on other lines
  // aren't discarded. materialsOverride holds that merged result once an
  // add has happened; MaterialTable is remounted (via key) to seed from it.
  const [materialsOverride, setMaterialsOverride] = useState<Material[] | null>(null);
  const [materialsReloadKey, setMaterialsReloadKey] = useState(0);
  // start-from-po-refresh: bumped when the PO check records or removes a PO,
  // so the Start-from-PO strip re-reads what it offers.
  const [poListKey, setPoListKey] = useState(0);
  // A different quote's local override must never leak into this one. Keyed
  // on both params so navigating between a saved quote and a mock one, or
  // between two saved quotes, clears it too.
  useEffect(() => { setMaterialsOverride(null); }, [id, savedId]);
  const displayMaterials = materialsOverride ?? initialMaterials;

  // PUTTING ADOPTED ROWS ON SCREEN, which is not just setRows.
  //
  // MaterialTable seeds its own copy from `initial` ONCE (a lazy useState
  // initializer; nothing re-reads it) and pushes changes UP. So setting this
  // component's `rows` alone leaves the table showing the pre-save values --
  // the share would be in state and invisible, and the next cell edit would
  // push the table's copy back over it. The override + remount is the same
  // mechanism Add Materials and the draft restore already use for exactly
  // this reason.
  //
  // The remount is bounded: adoptSavedPricing only reports `adopted` when a
  // share actually CHANGED, which is the first save after an assignment and
  // not the steady state.
  const applyAdoptedPricing = (adopted: { rows: Material[]; live: Map<number, LivePricedLine> }) => {
    setMaterialsOverride(adopted.rows);
    setMaterialsReloadKey((k) => k + 1);
    setRows(adopted.rows);
    // The engine numbers the save just stored, so the live columns show what
    // is in the database rather than what the last stateless price returned
    // for a different freight.
    setLivePricing(adopted.live);
    setLiveStatus("ok");
  };

  const warnings = useMemo(() => computeWarningsSummary(rows), [rows]);

  // ---- removing a line ---------------------------------------------------
  //
  // The inverse of handleMaterialsAdded below, and it takes the same route:
  // write the durable store, then set materialsOverride and force a remount,
  // because MaterialTable seeds its own `rows` from `initial` once and never
  // re-seeds. Setting this page's `rows` alone would leave the table showing
  // the line that was just removed.
  //
  // THE REMOVED ROW COMES FROM `rows`, NOT THE STORE. `rows` is the live,
  // possibly-edited table state, so the row captured for Undo carries the
  // qty/discount/margin the user had actually typed. Restoring the store's
  // copy instead would put the line back with those edits silently reverted.
  //
  // NOTHING IS SENT ANYWHERE HERE. A removal reaches Odoo by being absent
  // from the next save's payload: the backend replaces a quote's lines
  // wholesale (unlink + recreate) and re-allocates freight across whatever
  // survives. That save must carry quote_id/draftId or it creates a SECOND
  // quote and the removal lands on nothing — saveQuoteToBackend and saveDraft
  // both already do, and the debounced autosave below picks this change up
  // like any other.
  //
  // The live columns going blank straight afterwards is correct, not a
  // regression: pricingSignature covers every row, so dropping one makes the
  // stored engine numbers describe inputs that no longer exist. They come
  // back on the existing "Update prices" or on Save. Nothing here calls the
  // pricing endpoint — a request per click would price a quote the user is
  // still in the middle of editing.
  const [removedLine, setRemovedLine] = useState<{ row: Material; index: number } | null>(null);

  // A different quote's removal must never be undoable into this one.
  useEffect(() => { setRemovedLine(null); }, [id, savedId]);

  const handleRemoveLine = (lineNo: number) => {
    // Guarded here as well as on the button. The control is disabled at one
    // line, but the guard is what actually holds: deleting to empty would
    // leave the last line stranded in Odoo, because every save path
    // short-circuits on an empty list and the backend refuses one anyway.
    if (rows.length <= 1) return;

    // The store, so the removal survives navigating away and back in this
    // tab — the same durability addMaterialsToQuote gives an add.
    api.removeMaterialFromQuote(storeKey, lineNo);

    // The screen, from the live rows. Renumbering runs through the same
    // helper the store used, so the two cannot disagree about line numbers.
    const { rows: nextRows, removed, index } = removeLineFromRows(rows, lineNo);
    if (!removed) return;

    setRemovedLine({ row: removed, index });
    setMaterialsOverride(nextRows);
    setMaterialsReloadKey((k) => k + 1);
    setRows(nextRows);
  };

  // oem-quote-register: "Use OEM quote price" on a draft line. The pick sets
  // the NET COST (the competitive-bid shape: no list price, no percentage),
  // the lead time and the link; the engine prices from the net cost exactly
  // as from a typed one. The server re-checks the link on every save.
  const [oemLineNo, setOemLineNo] = useState<number | null>(null);
  const applyOemRows = (next: Material[]) => {
    const recomputed = recomputeAll(next);
    setMaterialsOverride(recomputed);
    setMaterialsReloadKey((k) => k + 1);
    setRows(recomputed);
  };
  const pickOemMatch = (lineNo: number, m: OemQuoteMatch) => {
    applyOemRows(rows.map((r) => (r.lineNo !== lineNo ? r : {
      ...r,
      netPrice: m.unit_price,
      netPriceEntered: true,
      listPrice: undefined,
      discountPct: undefined,
      discountRecorded: undefined,
      discountSource: "oem_quote" as const,
      discountRuleId: undefined,
      discountRulePairing: undefined,
      oemQuote: oemLinkFromMatch(m),
      leadTime: m.lead_time ?? r.leadTime,
      priceStatus: r.priceStatus === "unpriced" ? undefined : r.priceStatus,
      priceStatusReason: r.priceStatus === "unpriced" ? undefined : r.priceStatusReason,
    })));
    setFlashLineNos([lineNo]);
    setOemLineNo(null);
  };
  const stopUsingOem = (lineNo: number) => {
    applyOemRows(rows.map((r) => (r.lineNo !== lineNo ? r
      : { ...r, discountSource: "manual" as const, oemQuote: undefined })));
    setOemLineNo(null);
  };

  const undoRemoveLine = () => {
    if (!removedLine) return;
    api.restoreMaterialToQuote(storeKey, removedLine.row, removedLine.index);
    const restored = restoreLineToRows(rows, removedLine.row, removedLine.index);
    setRemovedLine(null);
    setMaterialsOverride(restored);
    setMaterialsReloadKey((k) => k + 1);
    setRows(restored);
    setFlashLineNos([removedLine.row.lineNo]);
    setRevealLine({ lineNo: removedLine.row.lineNo });
    setLineFilter("");                       // the restored line must be on screen
  };

  const handleMaterialsAdded = (result: AddMaterialsResult) => {
    const addedQtyByLine = new Map(result.bumpedLines.map((b) => [b.lineNo, b.addedQty]));
    const merged = [
      ...rows.map((r) => (addedQtyByLine.has(r.lineNo) ? { ...r, qty: r.qty + addedQtyByLine.get(r.lineNo)! } : r)),
      ...result.newLines,
    ];
    setMaterialsOverride(merged);
    setFlashLineNos(result.touchedLineNos);
    // Jump to the page holding the new line (or the first bumped one).
    const revealTo = result.newLines[0]?.lineNo ?? result.touchedLineNos[0];
    if (revealTo !== undefined) setRevealLine({ lineNo: revealTo });
    // Adding clears the filter: the new line is shown, not hidden by it.
    setLineFilter("");
    setMaterialsReloadKey((k) => k + 1);
  };

  // ---- THE PO'S LINES, ONTO THE QUOTE --------------------------------
  //
  // WARNING: THIS IS THE STEP THAT WAS MISSING. save_quote_po writes
  // anixify.po and anixify.po.line and "touches no quote line" -- its own
  // docstring says so -- so a confirmed PO left the quote itself empty. The
  // strip builds the rows (poLinesToQuoteRows, which carries no price from
  // the PO) and this puts them on the quote.
  //
  // WARNING: THROUGH saveDraft, THE EXISTING DRAFT SAVE, AND NOT A NEW WRITE
  // PATH. Same endpoint, same envelope, same engine pricing on the far side,
  // same concurrency token. The lines are non-empty by construction here, so
  // the client-side lineless guard does not apply and the backend takes it as
  // an ordinary draft update.
  //
  // WARNING: IT THROWS RATHER THAN SWALLOWING. The caller shows the reason
  // and leaves its preview up, so a refused add is visibly a refused add --
  // the alternative is a button that appears to work and a quote that is
  // still empty.
  const addPoLinesToQuote = async (poRows: Material[]) => {
    const target = poQuoteId;
    if (target === undefined || !quoteForSave) {
      throw new Error("This quote has not been saved yet.");
    }

    // Appended to whatever is on screen. In practice that is nothing -- the
    // offer only appears while the quote has no lines -- but appending is
    // what makes it correct rather than only currently-correct.
    const next = recomputeAll([...rows, ...poRows]);

    const result = await saveDraft(
      quoteForSave, next, VARIANCE_PCT, gpPct, target, ctxRef.current,
      shipmentsRef.current, saveTokenRef.current,
    );
    if (!result.ok) {
      throw new Error(result.error ?? "The backend refused the lines.");
    }

    // Adopt what the save left behind, on the same terms the autosave does:
    // the token so the next write is not refused as stale, and the id so a
    // later tick updates this row rather than adding another.
    if (result.saveToken !== undefined) saveTokenRef.current = result.saveToken;
    if (result.quoteId !== undefined) setDraftId(result.quoteId);

    // The table is seeded from `initial` on mount, so the reload key is what
    // makes new rows visible -- the same mechanism Add Materials and the
    // saved-quote load both already use.
    setMaterialsOverride(next);
    setRows(next);
    setMaterialsReloadKey((k) => k + 1);
  };

  // ---- MARGIN APPROVAL (margin-approval) ----------------------------------
  //
  // REQUEST saves the lines on screen as the draft FIRST (the refused
  // finalise wrote nothing), then asks with that save's token -- so what is
  // locked for approval is exactly what the requester was looking at.
  // "SAVE DRAFT": saves only -- no number, no finalise. The same draft save
  // the autosave makes, through the same lane, now.
  const finaliseIsApproval = !alreadyFinal && gpNeedsApproval && !adminView.isAdmin;
  // App review P2-4: a draft with an unpriced line cannot be finalised, so the
  // button says so (disabled + its reason) -- the banner names the lines.
  const finaliseUnpriced = !alreadyFinal && unpricedIssues.length > 0;
  // App review P2-6: the header margin is the ENGINE's (the side panel's own
  // figure, all lines priced), or "—". It used to print the quote object's
  // placeholder margin: 0.0% on every reopened quote.
  const headerMargin = computeEngineMarginSummary(rows, { ...ctx, negotiationBuffer: 0 }, livePricing);
  const saveDraftReason = finalisedElsewhere
    ? "This quote was finalised elsewhere and can no longer be saved from here."
    : rows.length === 0
      ? "Add at least one line before saving."
      : unpricedIssues.length > 0
        ? unpricedMessage(unpricedIssues)
        : undefined;
  const saveDraftDisabled = draftSaveState === "saving" || saveDraftReason !== undefined;
  const draftStatusText = draftSaveState === "saving"
    ? "Saving draft…"
    : draftSaveState === "error"
      ? ""
      : hasUnsavedChanges
        ? "Unsaved changes"
        : lastSavedSig !== null ? "Draft saved" : "";

  const saveDraftNow = async () => {
    if (!quoteForSave || rows.length === 0) return;
    const unpriced = unpricedProblems(rows);
    if (unpriced.length > 0) {
      setDraftSaveError(unpricedMessage(unpriced));
      setDraftSaveState("error");
      return;
    }
    const basis = freightBasisProblems(shipmentsRef.current, rows, quoteForSave.pricingMode);
    if (basis.length > 0) {
      setDraftSaveError(freightBasisMessage(basis));
      setDraftSaveState("error");
      return;
    }
    setDraftSaveState("saving");
    setDraftSaveError("");
    const sig = draftSignature;
    await saveLaneRef.current?.whenIdle();
    const ticket = saveLaneRef.current?.begin("manual", false);
    let result;
    try {
      result = await saveDraft(
        quoteForSave, rows, VARIANCE_PCT, gpPct, poQuoteId ?? draftId, ctxRef.current,
        shipmentsRef.current, saveTokenRef.current,
      );
    } finally {
      ticket?.done();
    }
    if (!result.ok) {
      if (result.quoteFinalised) setFinalisedElsewhere(true);
      setDraftSaveError(result.quoteStale
        ? "This quote was changed elsewhere since you opened it. Reopen it to get the latest version."
        : result.error ?? "The draft was not saved.");
      setDraftSaveState("error");
      return;
    }
    if (result.saveToken !== undefined) saveTokenRef.current = result.saveToken;
    if (result.quoteId !== undefined && !ticket?.superseded()) setDraftId(result.quoteId);
    setLastSavedSig(sig);
    setDraftSaves((n) => n + 1);
    setDraftRefusal("");
    setDraftSaveState("saved");
  };

  const requestApprovalNow = async () => {
    // ⚠️ THE ROW MAY NOT EXIST YET. On /quotes/:id the draft's id lives in
    // `draftId` (savedQuoteId and quote.odooQuoteId are both unset until the
    // quote is reopened), and before the first autosave tick there is no row
    // at all. The request used to read poQuoteId alone and return when it was
    // unset -- the button did nothing and said nothing. Found by the staging
    // browser tests (2026-09-24). The draft save below now CREATES the row
    // when there is none, exactly as the finalise press does.
    if (!quoteForSave) return;
    const known = poQuoteId ?? draftId;
    setApprovalBusy(true);
    setApprovalError("");
    // The same lane as the autosave and the finalise press: wait for any
    // in-flight tick, then go last, so a tick cannot create a second row.
    await saveLaneRef.current?.whenIdle();
    const ticket = saveLaneRef.current?.begin("manual", false);
    let saved;
    try {
      saved = await saveDraft(
        quoteForSave, rows, VARIANCE_PCT, gpPct, known, ctxRef.current,
        shipmentsRef.current, saveTokenRef.current,
      );
    } finally {
      ticket?.done();
    }
    const target = known ?? saved.quoteId;
    if (!saved.ok || target === undefined) {
      setApprovalBusy(false);
      setApprovalError(saved.error ?? "The draft was not saved, so approval was not requested.");
      return;
    }
    if (!isSavedMode && !ticket?.superseded()) setDraftId(target);
    if (saved.saveToken !== undefined) saveTokenRef.current = saved.saveToken;
    const result = await requestApproval(target, saveTokenRef.current);
    setApprovalBusy(false);
    if (!result.ok) {
      setApprovalError(result.error ?? "Approval was not requested.");
      return;
    }
    setApprovalRefusal(undefined);
    // Outside saved mode `quote` is the local store object and carries no
    // approval state, so re-reading would change nothing on screen: open the
    // saved record instead, which shows the pending lock.
    if (!isSavedMode) {
      setApprovalOpenId(target);
      return;
    }
    setApprovalReload((k) => k + 1);
  };

  // ---- QUOTE OUTCOME (quote-won-lost) --------------------------------------
  const runOutcomeAction = async (
    action: (quoteId: number) => Promise<OutcomeActionResult>,
    failure: string,
  ) => {
    const target = poQuoteId;
    if (target === undefined) return;
    setOutcomeBusy(true);
    setOutcomeError("");
    const result = await action(target);
    setOutcomeBusy(false);
    if (!result.ok || !result.outcome) {
      setOutcomeError(result.error ?? failure);
      return;
    }
    setOutcomeOverride(result.outcome);
    setOutcomeTick((t) => t + 1);
  };

  const runApprovalAction = async (
    action: (quoteId: number) => Promise<{ ok: boolean; error?: string }>,
    failure: string,
  ) => {
    const target = poQuoteId;
    if (target === undefined) return;
    setApprovalBusy(true);
    setApprovalError("");
    const result = await action(target);
    setApprovalBusy(false);
    if (!result.ok) {
      setApprovalError(result.error ?? failure);
      return;
    }
    setApprovalReload((k) => k + 1);
  };

  // ---- THE STALE-RATE NOTICE (fx-rate-entry, option (i)) -------------------
  //
  // ⚠️ DRAFTS ONLY. A finalised quote's rate is pinned and must never be
  // offered a change, so `alreadyFinal` switches the notice off entirely.
  //
  // ⚠️ IT NOTICES; IT NEVER ACTS ON ITS OWN. A draft keeps the rate it was
  // priced at because a stated rate may be a forward contract, and the app
  // cannot tell one from a schedule-resolved rate. The person presses "Use
  // today's rate"; nothing else changes the rows. The proper fix -- a
  // set-marker recording where each rate came from -- is deferred (HANDOFF).
  const fxNotice = alreadyFinal
    ? null
    : staleRateNotice(rows, fxSchedule, supplierCurrency, pricingMode, todayIso());
  const [fxNoticeBusy, setFxNoticeBusy] = useState(false);
  const [fxNoticeError, setFxNoticeError] = useState("");

  // "USE TODAY'S RATE": clear the rate on the affected lines, so the save
  // OMITS it and the backend resolves it from the schedule -- the path
  // fx-zero-rate-trap made work -- then adopt what the backend resolved.
  //
  // Saved through saveDraft, the existing draft save, exactly as
  // addPoLinesToQuote is: /saved-quotes/:id has no autosave, and a person
  // who pressed this expects the draft to BE re-priced, not to be re-priced
  // only if they later remember to press Save.
  const applyTodaysRate = async () => {
    // ⚠️ poQuoteId ALONE IS NOT ENOUGH (fx-use-today-fresh). On /quotes/:id
    // the draft's id lives in `draftId`, and before the first autosave tick
    // there is no row at all -- the press returned here and did nothing.
    // Found by the staging browser tests (2026-09-24); the same shape as the
    // Request approval fix. The save below creates the row when none exists.
    if (!fxNotice || !quoteForSave) return;
    const known = poQuoteId ?? draftId;
    setFxNoticeBusy(true);
    setFxNoticeError("");
    // The same lane as the autosave: wait for any in-flight tick, then go
    // last, so a tick cannot create a second row or undo this one.
    await saveLaneRef.current?.whenIdle();
    const ticket = saveLaneRef.current?.begin("manual", false);
    const affected = new Set(fxNotice.lineNos);
    const cleared = recomputeAll(rows.map((r) =>
      // Cleared AND marked "schedule": from here on the line follows the
      // schedule on every draft save (fx-rate-source-marker).
      affected.has(r.lineNo)
        ? { ...r, conversionRate: undefined, rateSource: "schedule" as const }
        : r));
    let result;
    try {
      result = await saveDraft(
        quoteForSave, cleared, VARIANCE_PCT, gpPct, known, ctxRef.current,
        shipmentsRef.current, saveTokenRef.current,
      );
    } finally {
      ticket?.done();
    }
    setFxNoticeBusy(false);
    if (!result.ok) {
      // Nothing is changed on screen: the rows still hold the old rate, which
      // is what the database still holds.
      setFxNoticeError(result.error ?? "The draft was not re-priced.");
      return;
    }
    if (result.saveToken !== undefined) saveTokenRef.current = result.saveToken;
    if (result.quoteId !== undefined) setDraftId(result.quoteId);
    // Adopt the RESOLVED rate the backend stored, by position -- the save
    // answers in the order it was sent. A line the backend could not resolve
    // keeps no rate, and zeroRateMeansNoRate reads a stored 0 as exactly that.
    const saved = result.lines ?? [];
    const next = cleared.map((r, i) => {
      if (!affected.has(r.lineNo)) return r;
      const stored = (saved[i] as { conversion_rate?: number } | undefined)?.conversion_rate;
      return { ...r, conversionRate: zeroRateMeansNoRate(stored), rateSource: "schedule" as const };
    });
    const adopted = recomputeAll(next);
    setMaterialsOverride(adopted);
    setRows(adopted);
    setMaterialsReloadKey((k) => k + 1);
  };

  // ---- THE GUIDED DEAL (ux-guided-deal) -------------------------------------
  // ONE read of the OEM requests / orders for the progress bar AND the blocks
  // below it, so the two can never disagree. A recorded confirmation re-reads
  // it (dealTick), as Mark won already does (outcomeTick).
  const [dealTick, setDealTick] = useState(0);
  const oemViewSeen = useRef(false);
  const onOemView = useCallback((v: OemConfirmationsView | null) => {
    setOemView(v);
    if (oemViewSeen.current) setDealTick((t) => t + 1);
    oemViewSeen.current = true;
  }, []);
  // THE ROW THE OEM STEPS ATTACH TO: the PO panels' row, or -- for a quote
  // created in this tab -- the draft its autosave wrote (draftId, the same id
  // Discard and the OEM-quote dialog already use). Without it a new quote
  // showed no OEM panels until it was reopened.
  const oemQuoteId = savedQuoteId ?? quote?.odooQuoteId ?? draftId;
  const oemReload = approvalReload + outcomeTick + draftSaves;
  const deal = useOemDealData(oemQuoteId, oemReload + dealTick);
  // M4 (D195): receipts, delivery notes, customs and the close-out state --
  // read only once the quote has an OEM order (the steps that use them).
  const fulfilRead = useFulfilmentData(oemQuoteId, oemReload + dealTick + deal.tick, deal.orders.length > 0);
  // A receipt changes the ORDERS too (received quantities, derived status):
  // refreshing the deal re-reads both (the fulfilment read follows deal.tick).
  const fulfil = { ...fulfilRead, refresh: deal.refresh };
  const [gateRefusals, setGateRefusals] = useState<Record<string, GateRefusal[]>>({});
  const onGateRefusals = useCallback((oem: string, refusals: GateRefusal[] | null) => {
    setGateRefusals((prev) => {
      const next = { ...prev };
      if (refusals && refusals.length) next[oem] = refusals; else delete next[oem];
      return next;
    });
  }, []);
  const [openSignal, setOpenSignal] = useState<Partial<Record<StepKey, number>>>({});
  const [openWon, setOpenWon] = useState(0);
  const [openLost, setOpenLost] = useState(0);
  const [openRecord, setOpenRecord] = useState(0);
  const [openOrder, setOpenOrder] = useState<{ id: number; signal: number } | null>(null);
  // M2: the line whose offers are being compared (its backend line id), or null.
  const [compareLine, setCompareLine] = useState<number | null>(null);
  // The last line + row the open comparison showed: a reload of the offers (after
  // "Ask" or "Use this offer") must never close and reopen the dialog mid-click.
  const compareShown = useRef<{ ol: OemOffersLine; row: Material } | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [cardError, setCardError] = useState("");
  const goToStep = useCallback((key: StepKey) => {
    setOpenSignal((o) => ({ ...o, [key]: (o[key] ?? 0) + 1 }));
    // After the section has opened: bring it into view (jsdom has no scrolling).
    window.setTimeout(() => {
      const el = document.getElementById(sectionId(key));
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, []);

  // ⚠️ BEFORE EVERY OTHER RETURN, AND AFTER EVERY HOOK, because the record this page describes is
  // gone. (It sat above two useState calls once, which breaks React's
  // rule that hooks run in the same order every render -- moved.)
  // Gone. Any screen below would be reading state about a quote that no
  // longer exists -- most visibly the saved-mode reload, which would fetch
  // the deleted id and report it as "not available to you".
  if (deleted) return <Navigate to="/quotes" replace />;

  if (copiedToId !== undefined) return <Navigate to={`/saved-quotes/${copiedToId}`} />;
  if (approvalOpenId !== undefined) return <Navigate to={`/saved-quotes/${approvalOpenId}`} />;

  // Saved-mode load states come first: without them an in-flight or failed
  // read would fall through to the mock "quote not found" screen below and
  // blame the wrong thing.
  if (isSavedMode && savedStatus === "loading") {
    return (
      <div style={{ padding: spacing[24] }} data-testid="saved-quote-loading">
        <Link to="/quotes" className="inline-flex items-center gap-1"
              style={{ fontSize: typography.fontSize.base, color: semanticColors.accentTextOnTint }}>
          <ChevronLeft size={iconSize.lg} strokeWidth={iconStroke.regular} /> Back to Quotes
        </Link>
        <div style={{ marginTop: spacing[16], fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder }}>
          Loading quote #{savedId} from Odoo…
        </div>
      </div>
    );
  }

  if (isSavedMode && savedStatus === "error") {
    return (
      <div style={{ padding: spacing[24] }} data-testid="saved-quote-error">
        <Link to="/quotes" className="inline-flex items-center gap-1"
              style={{ fontSize: typography.fontSize.base, color: semanticColors.accentTextOnTint }}>
          <ChevronLeft size={iconSize.lg} strokeWidth={iconStroke.regular} /> Back to Quotes
        </Link>
        <div style={{ marginTop: spacing[16], fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold }}>
          {savedNotFound ? "Quote not available" : "Could not load quote"}
        </div>
        <div style={{ marginTop: spacing[4], fontSize: typography.fontSize.base, color: semanticColors.textSecondary, maxWidth: 640 }}>
          {savedNotFound
            // ⚠️ BOTH POSSIBILITIES, BECAUSE THE SERVER WILL NOT SAY WHICH. Since
            // TASK 2 a draft that is not yours answers exactly as a missing
            // quote, so "no such quote exists" would be false half the time.
            ? `Quote #${savedId} is not available to you. Either it does not exist (it may have been deleted), or it is a draft belonging to someone else, which only its owner and an Admin can open.`
            : savedLoadError}
        </div>
      </div>
    );
  }

  if (!quote) {
    // THE SAVED DRAFT, on the route the quote list opens it on -- proven
    // read-only by QuoteDetail.openDraft.test.tsx. `replace`, so Back does
    // not return to a reference this tab cannot show.
    if (refLookup?.status === "found" && refLookup.quoteId !== undefined) {
      return <Navigate to={`/saved-quotes/${refLookup.quoteId}`} replace />;
    }

    // ⚠️ NO BRANCH BELOW SAYS "NOT FOUND". Each one states only what is known.
    const titleStyle = {
      marginTop: spacing[16], fontSize: typography.fontSize.xxl,
      fontWeight: typography.fontWeight.semibold,
    };
    const bodyStyle = {
      marginTop: spacing[4], fontSize: typography.fontSize.base,
      color: semanticColors.textSecondary, maxWidth: 640,
    };
    const ref = <span className="tabular-nums">{id}</span>;
    const status = refLookup?.status;
    return (
      <div style={{ padding: spacing[24] }} data-testid="quote-ref-unresolved"
           data-state={status ?? "checking"}>
        <Link to="/quotes" className="inline-flex items-center gap-1"
              style={{ fontSize: typography.fontSize.base, color: semanticColors.accentTextOnTint }}>
          <ChevronLeft size={iconSize.lg} strokeWidth={iconStroke.regular} /> Back to Quotes
        </Link>

        {status === undefined && (
          <div style={{ ...bodyStyle, marginTop: spacing[16] }} data-testid="quote-ref-checking">
            Looking for your saved draft of {ref}…
          </div>
        )}

        {status === "none" && (
          <>
            <div style={titleStyle}>No saved draft under this reference</div>
            <div style={bodyStyle} data-testid="quote-ref-no-draft">
              {ref} is not among your saved drafts. A new quote lives only in the
              browser tab that created it until it is saved as a draft, about a
              second and a half after it has a line that can be priced. If the
              page was reloaded before then, nothing had been saved for it yet.
              If Save Quote was pressed, it was given a quote number and is in
              the quote list under that number. Drafts are private to the person
              who started them.
              {refLookup?.truncated && " Only your 200 most recent drafts were checked."}
            </div>
          </>
        )}

        {status === "ambiguous" && (
          <>
            <div style={titleStyle}>More than one saved draft under this reference</div>
            <div style={bodyStyle} data-testid="quote-ref-ambiguous">
              You have {refLookup?.count} saved drafts under {ref}. Rather than
              guess which one you meant, none of them has been opened. Pick the
              right one from the quote list.
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div style={titleStyle}>Could not check your saved drafts</div>
            <div style={bodyStyle} data-testid="quote-ref-lookup-failed">
              {refLookup?.error} The work behind {ref} may well be saved. Check
              the quote list, or try again.
            </div>
          </>
        )}

        {status !== undefined && (
          <div style={{ marginTop: spacing[16], display: "flex", alignItems: "center", gap: spacing[12] }}>
            <Link to="/quotes" data-testid="quote-ref-open-list"
                  style={{ fontSize: typography.fontSize.base, color: semanticColors.accentTextOnTint }}>
              Open the quote list
            </Link>
            {(status === "none" || status === "error") && (
              <button
                type="button"
                data-testid="quote-ref-check-again"
                onClick={() => setRefLookupTick((t) => t + 1)}
                style={{
                  height: heights.control, padding: `0 ${spacing[12]}px`,
                  background: colors.white, color: semanticColors.textSecondary,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  fontSize: typography.fontSize.base, cursor: "pointer",
                }}
              >
                Check again
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // The journey, from what this screen already holds (lib/dealProgress.ts).
  const dealView = deal.hidden ? null : (deal.view ?? oemView);
  const dealInputs: DealInputs = {
    saved: oemQuoteId !== undefined,
    finalised: alreadyFinal,
    quoteName: savedName ?? quote.quoteNo,
    validUntil: quote.validUntil ? formatDate(quote.validUntil) : null,
    lineCount: rows.length,
    unpricedCount: unpricedIssues.length,
    hasCustomer: !!(customerOverride?.entity ?? quote.customer?.entity),
    unsaved: hasUnsavedChanges,
    approvalState: quote.approval?.state,
    finaliseIsApproval,
    finaliseBlockedReasons: alreadyFinal ? [] : [
      ...(finalisedElsewhere ? ["This quote was finalised elsewhere and can no longer be saved from here."] : []),
      ...(finaliseBlocks.length > 0 ? [finaliseBlockedMessage(finaliseBlocks, supplierCurrency)] : []),
      ...(!gpValid ? ["Enter a GP % above 0 and below 100."] : []),
    ],
    canEdit: canEditByRole,
    isAdmin: adminView.isAdmin,
    oem: dealView,
    requests: deal.hidden || !deal.loaded ? null : deal.requests,
    orders: deal.hidden || !deal.loaded ? null : deal.orders,
    outcome: alreadyFinal ? (outcomeNow?.status ?? "open") : null,
    supersededBy: outcomeNow?.supersededBy?.name ?? null,
    gateRefusals,
    orderableQty: Object.values(orderableByOem(dealView)).flat().reduce((s, r) => s + r.remaining, 0),
    offers: deal.offers ? { offers: deal.offers.offers, oems: deal.offers.oems,
      maxOnALine: Math.max(0, ...deal.offers.lines.map((l) => l.offers.length)) } : null,
    fulfil: fulfil.hidden || !fulfil.loaded ? null
      : { status: fulfil.status, customs: fulfil.customs, grnCount: fulfil.grns.length, dnCount: fulfil.notes.length },
  };
  const dealSteps = deriveDealSteps(dealInputs);
  const dealStep = Object.fromEntries(dealSteps.map((st) => [st.key, st])) as Record<StepKey, (typeof dealSteps)[number]>;
  const nextAction: NextAction = deriveNextAction(dealInputs, dealSteps);
  const dealFocus = nextAction.step ?? focusStep(dealSteps)?.key;
  const onCardAct = async (kind: NextAction["kind"]) => {
    setCardError("");
    const qid = oemQuoteId;
    switch (kind) {
      case "add-materials": setPickerOpen(true); return;
      case "save-draft": void saveDraftNow(); return;
      case "finalise": setConfirmFinalise(true); return;
      case "request-approval": void requestApprovalNow(); return;
      case "approve": goToStep("finalise"); return;
      case "mark-won": setOpenWon((n) => n + 1); goToStep("decision"); return;
      case "mark-lost": setOpenLost((n) => n + 1); goToStep("decision"); return;
      case "record-confirmation": setOpenRecord((n) => n + 1); goToStep("oem"); return;
      case "place-order": case "fix-order": goToStep("order"); return;
      case "record-event":
        if (nextAction.orderId !== undefined) setOpenOrder({ id: nextAction.orderId, signal: Date.now() });
        goToStep("track"); return;
      case "receive-goods": case "record-customs": goToStep("receive"); return;
      case "dispatch": goToStep("deliver"); return;
      case "close-out": goToStep("close"); return;
      case "compare": {
        const l = (deal.offers?.lines ?? []).find((x) => x.offers.length > 1);
        if (l) setCompareLine(l.line_id);
        return;
      }
      case "request-prices": {
        if (qid === undefined) return;
        setCardBusy(true);
        const err = await requestOemPrices(qid);
        setCardBusy(false);
        if (err) setCardError(err); else { deal.refresh(); goToStep("oem"); }
        return;
      }
      case "mark-sent": {
        const r = deal.requests.find((x) => x.id === nextAction.requestId);
        if (!r) return;
        setCardBusy(true);
        const err = await markOemRequestSent(r);
        setCardBusy(false);
        if (err) setCardError(err); else if (err === null) deal.refresh();
        return;
      }
      default: return;
    }
  };

  return (
    <div
      data-testid="quote-detail-page"
      className="flex"
      style={narrow
        ? { flexDirection: "column", minHeight: "calc(100vh - 48px)" }
        : { height: "calc(100vh - 48px)" }}
    >
      {/* Main column */}
      {/* ⚠️ THIS COLUMN IS THE PAGE'S VERTICAL SCROLLER. The Material list
          has no scroll box of its own: its rows grow and this column scrolls,
          so the header, the banners, the table, its totals and the change
          notifications read as one document, while the side panels beside it
          stay put. The table's sticky header sticks to this column. */}
      <div className="flex flex-col" style={narrow
        ? { padding: spacing[12], minWidth: 0 }
        : { padding: spacing[24], flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto" }}>
        {narrow && (
          <div data-testid="quote-narrow-note" style={{
            marginBottom: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textMuted,
          }}>
            Editing works best on a larger screen.
          </div>
        )}
        {/* ONE DESTINATION. Both modes came from the same list once /quotes
            started reading the database, so the two-branch back-link would
            only ever have sent a saved quote through a redirect. */}
        <Link to="/quotes" data-testid="quote-detail-back"
              className="inline-flex items-center gap-1 self-start"
              style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[12] }}>
          <ChevronLeft size={iconSize.md} strokeWidth={iconStroke.regular} /> Quotes
        </Link>

        {/* Header strip.

            It WRAPS rather than overflows. The main column is squeezed from
            the right by two panels — Quote Analysis (360px expanded) and, on
            a priced line, the price explanation (420px) — and a single
            non-wrapping row of five metrics plus two buttons runs out of
            width long before those are open on a laptop. Flex items refuse
            to shrink below their content, so the old fixed row did not
            compress, it collided: the metrics ran straight into Save Quote
            and Add Materials.

            Everything here is allowed to give way EXCEPT the button group,
            which is flexShrink 0 — the two actions stay whole and clickable
            at every width. Nothing is hidden to achieve that; the metrics
            reflow onto a second line instead. */}
        <div data-testid="quote-header-strip"
             className="flex items-start justify-between"
             style={{ flexWrap: "wrap", gap: spacing[16],
                      paddingBottom: spacing[16], borderBottom: `1px solid ${semanticColors.border}` }}>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div data-testid="quote-detail-number"
                 style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                          letterSpacing: typography.letterSpacing.tight, fontVariantNumeric: "tabular-nums" }}>
              {/* Once saved, show the reference Odoo actually assigned. The
                  local quoteNo is a client-side mock number and, for a new
                  quote, is not what the stored record is called. */}
              {savedName ?? quote.quoteNo}
            </div>
            <div data-testid="quote-detail-parties"
                 style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}>
              {/* The customer, and the ship-to when one is chosen. The
                  integrator that used to sit here was client-side only and
                  never persisted, so it named nothing the backend knew. */}
              {quote.customer?.entity?.name ?? quote.endClient}
              {quote.customer?.shipping?.name && (
                <>
                  <span style={{ margin: `0 ${spacing[8]}px`, color: semanticColors.textPlaceholder }}>•</span>
                  {quote.customer.shipping.name}
                </>
              )}
              {/* EDIT THE CUSTOMER'S BILLING DETAILS -- on the customer
                  record, not this quote. Sales/Admin; hidden for a Viewer,
                  and the route refuses one regardless. Offered on a finalised
                  quote too: it fills blanks on the customer, and the quote's
                  numbers are untouched. */}
              {/* copy-quote: where this draft came from. */}
              {/* quote-revisions: a revision says what it revises (and so
                  what it replaces when finalised); otherwise a copy says
                  where it came from. */}
              {quote.revision?.revisionOf ? (
                <span data-testid="quote-revision-of" style={{ marginLeft: spacing[8], color: semanticColors.textMuted }}>
                  {quote.status === "Draft"
                    ? `Revision of ${quote.revision.revisionOf.name} — finalising it supersedes that quote`
                    : `Revision ${quote.revision.revisionNo} of ${quote.revision.revisionOf.name}`}
                </span>
              ) : quote.copiedFrom && (
                <span data-testid="quote-copied-from" style={{ marginLeft: spacing[8], color: semanticColors.textMuted }}>
                  Copied from {quote.copiedFrom.name}
                </span>
              )}
              {/* project-id: the stored project, shown only when there is one. */}
              {(quote.projectId || quote.projectName) && (
                <span data-testid="quote-project" style={{ marginLeft: spacing[8], color: semanticColors.textMuted }}>
                  {[quote.projectId, quote.projectName].filter(Boolean).join(" · ")}
                </span>
              )}
              {quote.revision?.openRevisionDraft && (
                <span data-testid="quote-open-revision-draft" style={{ marginLeft: spacing[8], color: semanticColors.textMuted }}>
                  Revision draft open:{" "}
                  <Link to={`/saved-quotes/${quote.revision.openRevisionDraft.id}`}>
                    {quote.revision.openRevisionDraft.name}
                  </Link>
                </span>
              )}
              {canEditCustomers && quote.customer?.entity?.id && (
                <button
                  data-testid="quote-customer-details"
                  onClick={() => setCustomerDetailsOpen(true)}
                  style={{
                    marginLeft: spacing[8], padding: `0 ${spacing[8]}px`, height: heights.pill,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                    fontSize: typography.fontSize.xs, cursor: "pointer",
                  }}
                >
                  Customer details
                </button>
              )}
            </div>
          </div>
          <div className="flex items-start"
               style={{ gap: spacing[16], flexWrap: "wrap", justifyContent: "flex-end",
                        flex: "1 1 auto", minWidth: 0 }}>
            <div className="flex items-start"
                 style={{ columnGap: spacing[20], rowGap: spacing[12],
                          flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0 }}>
              {/* The one control that puts a quote on the import path. Every
                  mode-dependent thing downstream — the table's columns, the
                  pricing request, the line mapping, the staleness guard —
                  reads the mode this sets. */}
              <LabelValue label="Pricing" testid="header-pricing-mode">
                <div
                  data-testid="quote-pricing-mode"
                  className="flex"
                  style={{
                    border: `1px solid ${semanticColors.border}`,
                    borderRadius: radius.base, overflow: "hidden",
                  }}
                >
                  {(["domestic", "import"] as PricingMode[]).map((m) => {
                    const active = pricingMode === m;
                    return (
                      <button
                        key={m}
                        data-testid={`quote-pricing-mode-${m}`}
                        aria-pressed={active}
                        onClick={() => changePricingMode(m)}
                        // Locked on a finalised quote: the mode picks the
                        // pricing chain, so it feeds the total.
                        disabled={frozen}
                        style={{
                          height: heights.pill, padding: `0 ${spacing[10]}px`, border: "none",
                          backgroundColor: active ? colors.indigo600 : semanticColors.surface,
                          color: active ? semanticColors.onAccent : semanticColors.textSecondary,
                          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
                          textTransform: "capitalize", cursor: "pointer",
                        }}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </LabelValue>
              {/* SUPPLIER CURRENCY. Beside the mode because it describes the
                  same thing -- the whole quote -- and is only meaningful once
                  the mode is import.

                  Shows the ISO CODE as text, deliberately. Rendering the
                  symbol would mean deciding which figures on the screen are
                  supplier-side and which are rupees, and getting decimal
                  places right per currency; that is a later slice. A code is
                  unambiguous and needs neither. */}
              {pricingMode === "import" && (
                <LabelValue label="Supplier Currency" testid="header-supplier-currency">
                  <select
                    data-testid="quote-supplier-currency"
                    value={supplierCurrency ?? ""}
                    disabled={!canEdit || frozen}
                    onChange={(e) => changeSupplierCurrency(e.target.value)}
                    style={{
                      height: heights.pill, padding: `0 ${spacing[6]}px`,
                      border: `1px solid ${semanticColors.border}`,
                      borderRadius: radius.base,
                      backgroundColor: semanticColors.surface,
                      color: supplierCurrency ? semanticColors.textPrimary : semanticColors.textMuted,
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.medium,
                      cursor: canEdit ? "pointer" : "default",
                    }}
                  >
                    {/* "Not recorded" is a real state and stays selectable-looking
                        rather than being silently replaced by a default. */}
                    <option value="">Not recorded</option>
                    {currencies.map((c) => (
                      <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                  </select>
                </LabelValue>
              )}
              <LabelValue label="Status" testid="header-status"><StatusPill status={headerStatus ?? quote.status} /></LabelValue>
              {/* WHO FINALISED IT. Finalising spends a number and cannot be
                  undone, so it is attributed. "unknown" for a quote finalised
                  before this was recorded -- never a guess. */}
              {/* margin-approval: who approved a below-limit quote. */}
              {quote.approval?.state === "approved" && (
                <LabelValue label="Approved by" testid="header-approved-by">
                  {quote.approval.approvedBy ?? "unknown"}
                  {quote.approval.approvedAt ? ` · ${formatDate(quote.approval.approvedAt.slice(0, 10))}` : ""}
                </LabelValue>
              )}
              {finaliser && (
                <LabelValue label="Finalised by" testid="header-finalised-by">
                  {finaliser.by ?? "unknown"}
                  {finaliser.at ? ` · ${formatDate(finaliser.at.slice(0, 10))}` : ""}
                </LabelValue>
              )}
              {/* WHO LAST EDITED IT AFTER FINALISING (TASK 6). Only notes can
                  change on a finalised quote, but it is a document someone
                  may already hold, so the edit is attributed. Absent when it
                  has not been edited since finalising (or since recorded). */}
              {finaliser?.editedBy && (
                <LabelValue label="Edited after finalising" testid="header-final-edited-by">
                  {finaliser.editedBy}
                  {finaliser.editedAt ? ` · ${formatDate(finaliser.editedAt.slice(0, 10))}` : ""}
                </LabelValue>
              )}
              {/* Only when there IS a validity: a finalised quote's stored
                  date (quote-validity). A draft has none. */}
              {quote.validUntil && (
                <LabelValue label="Valid Until" testid="header-valid-until">{formatDate(quote.validUntil)}</LabelValue>
              )}
              <LabelValue label="Total Value" testid="header-total-value" large>{formatRupees(quote.value)}</LabelValue>
              <LabelValue label="Margin" testid="header-margin" large>{headerMargin ? formatPercent(headerMargin.realisedMarginPct) : "—"}</LabelValue>
              {/* DISPATCH FROM (warehouse-master). Editable on a draft by whoever
                  may edit it; FTWZ sites are listed greyed and cannot be
                  chosen; a finalised quote shows the warehouse it was issued
                  from (frozen with its tax). The server refuses an inactive or
                  FTWZ warehouse regardless. */}
              <LabelValue label="Dispatch from" testid="header-dispatch">
                {canEdit && !frozen ? (
                  <select
                    data-testid="quote-dispatch-select"
                    value={dispatchValue === null || dispatchValue === undefined ? "" : String(dispatchValue)}
                    onChange={(e) => setWarehouseOverride(e.target.value === "" ? null : Number(e.target.value))}
                    style={{
                      height: heights.input, maxWidth: layout.searchMinWidthWide, padding: `0 ${spacing[6]}px`,
                      border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                      fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
                    }}
                  >
                    <option value="">{dispatchValue === undefined && defaultWarehouseLabel
                      ? `Default: ${defaultWarehouseLabel}` : "Not chosen"}</option>
                    {warehouses
                      .filter((w) => w.active || w.id === dispatchValue)
                      .map((w) => (
                        <option key={w.id} value={String(w.id)} disabled={!w.selectable}
                                data-testid={`quote-dispatch-option-${w.code}`}>
                          {warehouseOptionLabel(w)}
                        </option>
                      ))}
                  </select>
                ) : (
                  <span data-testid="quote-dispatch-readonly">{dispatchReadonly ?? "—"}</span>
                )}
              </LabelValue>
              {/* GP % PER QUOTE (gp-per-quote). Editable on a draft by whoever
                  may edit it; read-only when finalised or awaiting approval --
                  the server refuses both regardless. */}
              <LabelValue label="GP %" testid="header-gp">
                {canEdit && !frozen ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: spacing[8] }}>
                    <input
                      data-testid="quote-gp-input"
                      type="number" min={0} max={100} step={0.1}
                      value={gpInput}
                      onChange={(e) => setGpText(e.target.value)}
                      aria-invalid={!gpValid}
                      style={{
                        width: layout.gpInputWidth, height: heights.input, padding: `0 ${spacing[6]}px`,
                        border: `1px solid ${gpValid ? semanticColors.border : semanticColors.destructiveBorder}`,
                        borderRadius: radius.base, fontSize: typography.fontSize.base,
                      }}
                    />
                    {!gpValid && (
                      <span data-testid="quote-gp-invalid" role="alert"
                            style={{ fontSize: typography.fontSize.xs, color: semanticColors.destructive }}>
                        Above 0 and below 100
                      </span>
                    )}
                    {gpNeedsApproval && (
                      <span data-testid="quote-gp-needs-approval" style={{
                        fontSize: typography.fontSize.xs, padding: `0 ${spacing[6]}px`,
                        backgroundColor: semanticColors.warningBg, color: semanticColors.warningTextOnTint,
                        border: `1px solid ${semanticColors.warningBorder}`, borderRadius: radius.base,
                      }}>
                        Needs approval (limit {formatPercent(approvalLimit as number)})
                      </span>
                    )}
                  </span>
                ) : (
                  <span data-testid="quote-gp-readonly">{formatPercent(storedGp)}</span>
                )}
              </LabelValue>
            </div>
            <div className="flex items-center" style={{ gap: spacing[8], flexShrink: 0, flexWrap: narrow ? "wrap" : undefined }}>
            {/* Disabled once another tab finalised this quote.
                ⚠️ THIS IS A UI GUARD, NOT A GUARANTEE, and the difference is
                worth knowing. The backend refuses the AUTOSAVE (a draft-ward
                write onto a final quote) but NOT this press, which sends
                is_draft: false -- final -> final reads as an ordinary edit of
                a saved quote, so a stale tab pressing Save would replace the
                finalised quote's lines with the ones on this screen. Leaving
                the button live under a banner saying the quote can no longer
                be written to would be the screen contradicting itself, so it
                is disabled; but an older bundle, or a second stale tab, still
                gets through. The real fix is server-side -- see BF5c-2f-iv. */}
            {/* COPY TO NEW DRAFT (copy-quote). Sales and Admin; for Sales
                their own quotes and any finalised one -- the rule the backend
                applies (it answers another person's draft as missing). A
                Viewer never sees it and the route refuses one. */}
            {/* quote-revisions: Revise -- a finalised Open/Expired quote, the
                owner or an Admin, and no revision draft already open. */}
            {canEditByRole && quote.odooQuoteId !== undefined && quote.status !== "Draft"
              && (outcomeNow?.status === "open" || outcomeNow?.status === "expired")
              && !quote.revision?.openRevisionDraft && (
              <button
                data-testid="quote-header-revise"
                onClick={() => { void reviseThisQuote(); }}
                disabled={reviseBusy}
                title="A revision draft of this quote. When finalised it keeps this number with -R1, -R2…, and this quote becomes Superseded."
                style={{
                  height: heights.control, padding: `0 ${spacing[14]}px`,
                  backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                  cursor: reviseBusy ? "not-allowed" : "pointer",
                }}
              >
                {reviseBusy ? "Revising…" : "Revise"}
              </button>
            )}
            {/* discard-draft: a DRAFT only, for its owner or an Admin (canEdit
                also excludes Viewers and a quote awaiting approval). The server
                re-checks every rule. */}
            {canEdit && !alreadyFinal && discardId !== undefined && (
              <button
                data-testid="quote-header-discard"
                onClick={() => { setDiscardError(""); setDiscardOpen(true); }}
                title="Remove this draft and its lines permanently."
                style={{
                  height: heights.control, padding: `0 ${spacing[14]}px`,
                  backgroundColor: semanticColors.surface, color: semanticColors.destructive,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                  cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                Discard draft
              </button>
            )}
            {discardOpen && (
              <div data-testid="discard-backdrop"
                   onClick={(e) => { if (e.target === e.currentTarget && !discardBusy) setDiscardOpen(false); }}
                   style={{ position: "fixed", inset: 0, zIndex: 60, backgroundColor: semanticColors.overlay,
                            display: "flex", alignItems: "center", justifyContent: "center", padding: spacing[16] }}>
                <div data-testid="discard-dialog" role="alertdialog" aria-modal="true" aria-label="Discard draft"
                     style={{ width: layout.modalWidth, maxWidth: "100%", backgroundColor: colors.white,
                              borderRadius: radius.base, boxShadow: shadows.modal, padding: spacing[20],
                              display: "flex", flexDirection: "column", gap: spacing[12], whiteSpace: "normal" }}>
                  <div data-testid="discard-question" style={{ fontSize: typography.fontSize.base, color: semanticColors.textPrimary }}>
                    Discard draft {quote?.odooQuoteName ?? quote?.quoteNo ?? saved?.name ?? ""}? This removes it permanently.
                  </div>
                  {discardError && (
                    <div data-testid="discard-error" role="alert"
                         style={{ fontSize: typography.fontSize.sm, color: semanticColors.destructive }}>{discardError}</div>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[8] }}>
                    <button data-testid="discard-cancel" disabled={discardBusy} onClick={() => setDiscardOpen(false)}
                            style={{ height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
                                     border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button data-testid="discard-confirm" disabled={discardBusy} onClick={() => { void doDiscard(); }}
                            style={{ height: heights.control, padding: `0 ${spacing[14]}px`, border: "none",
                                     backgroundColor: semanticColors.destructive, color: colors.white,
                                     borderRadius: radius.base, cursor: discardBusy ? "not-allowed" : "pointer",
                                     fontWeight: typography.fontWeight.medium }}>
                      {discardBusy ? "Discarding…" : "Discard draft"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {canCopy && (
              <button
                data-testid="quote-header-copy"
                onClick={() => { void copyToNewDraft(); }}
                disabled={copyBusy}
                title="A new draft with this quote's customer and lines. Nothing is finalised; the copy re-prices."
                style={{
                  height: heights.control, padding: `0 ${spacing[14]}px`,
                  backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                  cursor: copyBusy ? "not-allowed" : "pointer",
                }}
              >
                {copyBusy ? "Copying…" : "Copy to new draft"}
              </button>
            )}
            {/* save-draft-finalise: what the draft's save state is, in words. */}
            {canEdit && !alreadyFinal && draftStatusText !== "" && (
              <span data-testid="quote-draft-status" role="status" style={{
                alignSelf: "center", fontSize: typography.fontSize.xs, whiteSpace: "nowrap",
                color: hasUnsavedChanges ? colors.amber800 : semanticColors.textMuted,
              }}>
                {draftStatusText}
              </span>
            )}
            {/* SAVE DRAFT: saves only. No number, no finalise. */}
            {canEdit && !alreadyFinal && <button
              data-testid="quote-header-save-draft"
              onClick={() => { void saveDraftNow(); }}
              disabled={saveDraftDisabled}
              title={saveDraftReason}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                cursor: saveDraftDisabled ? "default" : "pointer",
                opacity: saveDraftDisabled ? 0.6 : 1, whiteSpace: "nowrap",
              }}
            >
              {draftSaveState === "saving" ? "Saving…" : "Save draft"}
            </button>}
            {/* FINALISE: asks first, then issues the number. Below the
                approval limit (Sales) it is "Request approval", as the
                approval panel offers. A FINALISED quote keeps "Save Quote",
                which saves the fields still editable on it. */}
            {canEdit && <button
              data-testid="quote-header-save-quote"
              onClick={alreadyFinal
                ? saveQuote
                : finaliseIsApproval
                  ? () => { void requestApprovalNow(); }
                  : () => setConfirmFinalise(true)}
              disabled={saveStatus === "saving" || rows.length === 0 || finalisedElsewhere
                || finaliseBlocks.length > 0 || !gpValid || finaliseUnpriced}
              title={finalisedElsewhere
                ? "This quote was finalised elsewhere and can no longer be saved from here."
                : finaliseUnpriced
                  ? unpricedMessage(unpricedIssues)
                : finaliseBlocks.length > 0
                  ? finaliseBlockedMessage(finaliseBlocks, supplierCurrency)
                  // fx-rate-entry: said AT the press, never a block.
                  : fxNotice ? staleRateText(fxNotice) : undefined}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                cursor: finalisedElsewhere || finaliseBlocks.length > 0 || finaliseUnpriced ? "default" : "pointer",
                opacity: saveStatus === "saving" || rows.length === 0 || finalisedElsewhere
                  || finaliseBlocks.length > 0 || finaliseUnpriced ? 0.6 : 1,
              }}
            >
              {saveStatus === "saving"
                ? (alreadyFinal ? "Saving…" : "Finalising…")
                : alreadyFinal ? "Save Quote" : finaliseIsApproval ? "Request approval" : "Finalise quote"}
            </button>}
            {canEdit && !frozen && <button
              data-testid="quote-header-add-materials"
              className="btn-primary"
              onClick={() => setPickerOpen(true)}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                border: "none", borderRadius: radius.base,
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
              }}
            >
              Add Materials
            </button>}
            </div>
          </div>
        </div>

        {/* ux-guided-deal: where the deal is, and what to do now. */}
        <DealProgressBar steps={dealSteps} focus={dealFocus} onGo={goToStep} />
        <NextStepCard action={nextAction} busy={cardBusy} error={cardError}
          onAct={(kind) => { void onCardAct(kind); }} />


        {/* save-draft-finalise: the draft save's refusal, in the backend's words. */}
        {canEdit && !alreadyFinal && draftSaveState === "error" && draftSaveError !== "" && (
          <div data-testid="quote-draft-save-error" role="alert" style={{
            marginTop: spacing[8], padding: `${spacing[8]}px ${spacing[12]}px`,
            backgroundColor: semanticColors.destructiveBg, border: `1px solid ${semanticColors.destructiveBorder}`,
            color: semanticColors.destructive, borderRadius: radius.base, fontSize: typography.fontSize.sm,
          }}>
            Draft not saved. {draftSaveError}
          </div>
        )}

        {/* FINALISE CONFIRMATION: a number, once issued, is never taken back. */}
        {confirmFinalise && quote && (
          <div
            data-testid="quote-finalise-dialog"
            role="dialog" aria-modal="true" aria-labelledby="quote-finalise-title"
            onClick={() => setConfirmFinalise(false)}
            style={{
              position: "fixed", inset: 0, backgroundColor: semanticColors.overlay,
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: layout.modalWidth, maxWidth: overlayMaxWidth,
                backgroundColor: semanticColors.surface, border: `1px solid ${semanticColors.border}`,
                borderRadius: radius.base, boxShadow: shadows.modal, padding: spacing[20],
              }}
            >
              <div id="quote-finalise-title" data-testid="quote-finalise-text" style={{
                fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                lineHeight: typography.lineHeight.base,
              }}>
                <span style={{ fontWeight: typography.fontWeight.semibold }}>Finalise {quote.quoteNo}?</span>{" "}
                This issues a quote number and locks the figures.
              </div>
              {(() => {
                const cap = oemValidityCap(rows, companySettings?.quote_validity_days);
                return cap ? (
                  <div data-testid="quote-finalise-oem-cap" style={{
                    marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
                    backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
                    borderRadius: radius.base, color: colors.amber800, fontSize: typography.fontSize.sm,
                  }}>
                    Valid until will be {formatDate(cap)} because an OEM price expires then.
                  </div>
                ) : null;
              })()}
              {(() => {
                const warn = oemFinaliseWarning(oemView);
                return warn ? (
                  <div data-testid="quote-finalise-oem-unconfirmed" style={{
                    marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
                    backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
                    borderRadius: radius.base, color: colors.amber800, fontSize: typography.fontSize.sm,
                  }}>
                    {warn}
                  </div>
                ) : null;
              })()}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[8], marginTop: spacing[20] }}>
                <button
                  data-testid="quote-finalise-cancel"
                  onClick={() => setConfirmFinalise(false)}
                  style={{
                    height: heights.control, padding: `0 ${spacing[14]}px`,
                    backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  data-testid="quote-finalise-confirm"
                  className="btn-primary"
                  autoFocus
                  onClick={() => { setConfirmFinalise(false); void saveQuote(); }}
                  style={{
                    height: heights.control, padding: `0 ${spacing[14]}px`,
                    backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none",
                    borderRadius: radius.base, fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.medium, cursor: "pointer",
                  }}
                >
                  Finalise quote
                </button>
              </div>
            </div>
          </div>
        )}

        <SaveQuoteBanner
          status={saveStatus}
          quoteName={savedName}
          quoteId={savedQuoteId}
          grandTotal={savedGrandTotal}
          error={saveError}
        />

        {/* TASK 5 -- WHY SAVE QUOTE IS OFF. A title tooltip alone is not a
            visible reason. Shown to whoever could press Save; the backend
            refuses the same lines before it draws a number regardless. */}
        {canEdit && finaliseBlocks.length > 0 && (
          <div
            data-testid="quote-finalise-blocked"
            role="status"
            style={{
              marginTop: spacing[8], padding: `${spacing[8]}px ${spacing[12]}px`,
              backgroundColor: semanticColors.warningBg,
              border: `1px solid ${semanticColors.warningBorder}`,
              borderRadius: radius.base, color: semanticColors.warningTextOnTint,
              fontSize: typography.fontSize.sm,
            }}
          >
            {finaliseBlockedMessage(finaliseBlocks, supplierCurrency)}
          </div>
        )}

        {/* TASK 6 -- WHY THE FIGURES CANNOT BE EDITED. Shown to whoever could
            otherwise edit; a viewer cannot edit anything and needs no reason. */}
        {canEdit && frozen && quote && (
          <div
            data-testid="quote-frozen-notice"
            role="status"
            style={{
              marginTop: spacing[8], padding: `${spacing[8]}px ${spacing[12]}px`,
              backgroundColor: semanticColors.panelBg,
              border: `1px solid ${semanticColors.border}`,
              borderRadius: radius.base, color: semanticColors.textSecondary,
              fontSize: typography.fontSize.sm,
            }}
          >
            {savedName ?? quote.quoteNo} is finalised, so everything that makes up its
            total is locked: prices, quantities, discounts, freight, duty and
            conversion rates, the lines themselves, the shipments and the customer.
            Manufacturer, UoM, remarks and lead times can still be edited. A quote
            with different figures is a new quote.
          </div>
        )}

        {/* CUSTOMER PO DOCUMENTS (slice A). Storage here; reading and comparing are "Extract from PDF"
            and the comparison in PO check (slice B, built).

            ⚠️ IT NEEDS A PERSISTED QUOTE AND IS HIDDEN WITHOUT ONE, which is
            not a styling decision. These documents are attached to an
            anixify.quote ROW; a quote that has never been saved has no row and
            therefore nothing to attach to. Rendering the control anyway would
            offer an upload with nowhere to put it -- the dead-control pattern
            deadControls.test.tsx exists to keep out. `odooQuoteId` is written
            only from a real save response and is the app's single signal that
            the record exists; `savedQuoteId` covers the quote just saved in
            this session, before `quote` is re-read. */}
        {/* A QUOTE STARTED FROM A CUSTOMER PO -- the way out while it is
            still empty, and the customer it does not have yet.

            ⚠️ BEHIND THE SAME GATE AS THE TWO PANELS BELOW, and additionally
            only for a DRAFT. It reads and deletes an anixify.quote row, so an
            unsaved quote has nothing for it to act on; and a finalised quote
            has a customer by definition (17.0.1.35.0 refuses a finalise
            without one) and can never be deleted by this path, so the strip
            would have nothing to say about one.

            ⚠️ `alreadyFinal` IS REUSED RATHER THAN A SECOND DERIVATION OF THE
            SAME FACT. It is the screen's one answer to "is this quote final"
            -- a finalise from this tab, or a reopened non-draft -- and a
            parallel `isDraft` would be a second one to keep in step. */}
        {poQuoteId !== undefined && !alreadyFinal && (
          <StartFromPoStrip
            quoteId={poQuoteId}
            lineCount={rows.length}
            reloadKey={poListKey}
            canWrite={canEdit}
            onDeleted={() => setDeleted(true)}
            onChooseCustomer={setCustomerOverride}
            onAddLines={addPoLinesToQuote}
          />
        )}

        {poQuoteId !== undefined && <PoDocuments quoteId={poQuoteId} canWrite={canEdit} />}

        {/* THE PO CHECK (slice B), beside the documents panel and behind the
            SAME gate for the same reason: PO lines attach to an anixify.quote
            ROW, and an unsaved quote has none. It compares lines a person
            TYPED -- it does not read the uploaded document above it, and the
            panel says so. */}
        {poQuoteId !== undefined && (
          <PoComparison
            quoteId={poQuoteId} canWrite={canEdit} onOutcome={setOutcomeOverride}
            // po-deliver-to-match: the ship-to was set on the server; re-read
            // the quote so the screen (and the next save) carries it.
            onShipToSet={() => setApprovalReload((k) => k + 1)}
            onPosChanged={() => setPoListKey((k) => k + 1)}
          />
        )}


        {/* The losing tab, once another one finalised this quote. Rendered
            ALONGSIDE the save banner rather than instead of it: they answer
            different questions -- that one reports a button press, this one
            reports the state of the quote -- and this condition outlives any
            single press. `draftId` is the finalised record: finalising is a
            transition on the same row this tab was autosaving into. */}
        {finalisedElsewhere && <QuoteFinalisedBanner quoteId={draftId} />}

        {/* copy-quote: a refused or failed copy, in the backend's words. */}
        {copyError !== "" && (
          <div data-testid="quote-copy-error" role="alert" style={{
            marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
            backgroundColor: semanticColors.destructiveBg, border: `1px solid ${semanticColors.destructiveBorder}`,
            color: semanticColors.destructive, borderRadius: radius.base, fontSize: typography.fontSize.sm,
          }}>
            Not copied. {copyError}
          </div>
        )}
        {customerGstinNote !== "" && (
          <div
            data-testid="customer-gstin-note"
            style={{
              marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
              borderRadius: radius.base, backgroundColor: semanticColors.warningBg,
              border: `1px solid ${semanticColors.warningBorder}`,
              color: semanticColors.warningTextOnTint, fontSize: typography.fontSize.sm,
            }}
          >
            {customerGstinNote}
          </div>
        )}
        {/* gst-split: why the document cannot split CGST/SGST/IGST. In the
            APP only -- the document never prints it. */}
        {(pdfTax ?? quote?.tax)?.note && (
          <div
            data-testid="quote-tax-note"
            style={{
              marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
              borderRadius: radius.base, backgroundColor: semanticColors.warningBg,
              border: `1px solid ${semanticColors.warningBorder}`,
              color: semanticColors.warningTextOnTint, fontSize: typography.fontSize.sm,
            }}
          >
            {(pdfTax ?? quote?.tax)?.note} The document prints one GST line until this is filled in.
          </div>
        )}
        {reviseError !== "" && (
          <div data-testid="quote-revise-error" role="alert" style={{
            marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
            backgroundColor: semanticColors.destructiveBg, border: `1px solid ${semanticColors.destructiveBorder}`,
            color: semanticColors.destructive, borderRadius: radius.base, fontSize: typography.fontSize.sm,
          }}>
            Not revised. {reviseError}
          </div>
        )}

        {/* THE STALE-RATE NOTE. Amber, never a block: the Save / Finalise
            press stays exactly as available as it was, so this note is also
            what a person sees at Finalise, and it does not stop them. */}
        {fxNotice && (
          <div
            data-testid="fx-stale-note"
            style={{
              marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
              borderRadius: radius.base,
              backgroundColor: colors.amber50, border: `1px solid ${colors.amber200}`,
              color: colors.amber800, fontSize: typography.fontSize.sm,
              display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap",
            }}
          >
            <span data-testid="fx-stale-text">{staleRateText(fxNotice)}</span>
            {canEdit && (
              <button
                data-testid="fx-stale-use-today"
                onClick={() => { void applyTodaysRate(); }}
                disabled={fxNoticeBusy}
                style={{
                  height: heights.control, padding: `0 ${spacing[12]}px`,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  backgroundColor: semanticColors.surface, color: semanticColors.textPrimary,
                  fontSize: typography.fontSize.base,
                  cursor: fxNoticeBusy ? "not-allowed" : "pointer",
                }}
              >
                {fxNoticeBusy ? "Re-pricing…" : "Use today's rate"}
              </button>
            )}
            {fxNoticeError !== "" && (
              <span data-testid="fx-stale-error">{fxNoticeError}</span>
            )}
          </div>
        )}

        {/* THE AUTOSAVE WAS REFUSED, AND FOR ANY REASON BUT THE TWO ABOVE.
            Beside QuoteFinalisedBanner because it answers the same question --
            "is my work saved?" -- and the answer here is no. Cleared by the
            next successful tick, so it disappears the moment the cause is
            fixed rather than having to be dismissed.

            ⚠️ THE BACKEND'S OWN WORDING, VERBATIM. The refusal that matters
            most on this path tells the reader exactly what to do ("A customer
            called X already exists -- select it instead."), and a generic
            "could not save" would throw that away. */}
        {draftRefusal !== "" && (
          <div
            data-testid="draft-refusal"
            style={{
              marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
              borderRadius: radius.base,
              backgroundColor: colors.amber50,
              border: `1px solid ${colors.amber200}`,
              color: colors.amber800, fontSize: typography.fontSize.sm,
            }}
          >
            Not saved — {draftRefusal}
          </div>
        )}

        {/* BF5d-2. Alongside the save banner for the same reason
            QuoteFinalisedBanner is: that one reports a button press, this one
            reports a condition of the quote that both save paths are declining
            to send. Unlike QuoteFinalisedBanner it is DERIVED rather than
            latched -- fixing the basis or assigning a dimensioned line clears
            it with no further code. Renders nothing when there is no problem. */}
        {/* Both are derived and both can be up at once -- they describe
            different gaps and each names its own remedy. Unpriced first,
            matching the order the save seams check them in: a line with no
            cost is a more basic problem than how a cost gets split. */}
        {/* FIRST of the three, deliberately. The other two describe fixable
            gaps in a real quote; this one says the quote itself is not real,
            which changes how the reader should interpret anything below it. */}
        <ExampleDataBanner message={exampleDataBlockMessage} />
        <UnpricedBanner message={unpricedBlockMessage} />
        <FreightPendingBanner message={freightPendingBlockMessage} />
        <FreightBasisBanner message={freightBasisBlockMessage} />

        {quote.brandLock && (
          <div data-testid="quote-brand-lock"
               className="flex items-center"
               style={{ marginTop: spacing[12], padding: `${spacing[6]}px ${spacing[12]}px`,
                        backgroundColor: colors.indigo50, border: `1px solid ${colors.indigo100}`,
                        borderRadius: radius.base, gap: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.accentTextOnTint }}>
            <Lock size={iconSize.md} strokeWidth={iconStroke.regular} />
            <span style={{ color: colors.indigo800, fontWeight: typography.fontWeight.medium }}>Brand locked:</span>
            <span style={{ color: semanticColors.accentTextOnTint }}>{quote.brandLock}</span>
          </div>
        )}

        {/* Engine verification panel — DEVELOPMENT ONLY.
            Row-4 fixture check, engine status line, live/est. legend and the
            technical variance/GP caption. All of it is internal validation
            reporting, not quote information, so it is gated out of the
            normal user experience (?dev=1 or REACT_APP_DEV_TOOLS=1).

            Nothing behind it was removed: this panel's Recalculate button
            and the "Update prices" action in the Material list header below
            call the SAME runBackendPricing, which posts to the same
            endpoint and drives MaterialTable's "live" columns exactly as
            before. The "est." columns (freight/duty/landed cost) stay on
            pricing.ts — the backend does not compute those yet. */}
        {showDevTools && (
          <BackendPriceStrip
            status={liveStatus}
            error={liveError}
            pricedCount={livePricing.size}
            totalCount={rows.length}
            onRecalculate={runBackendPricing}
          />
        )}

        {/* A pricing failure stays user-facing even with the panel hidden:
            the live columns blank themselves rather than fall back to
            estimates, so without this the table would go quiet with no
            stated reason. Shown once, here, rather than duplicated when the
            dev panel is on — that panel renders its own copy. */}
        {!showDevTools && liveStatus === "error" && (
          <div
            data-testid="quote-pricing-error"
            className="flex items-start"
            style={{
              marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
              border: `1px solid ${semanticColors.destructiveBorder}`,
              backgroundColor: semanticColors.destructiveBg,
              borderRadius: radius.base, gap: spacing[8],
              fontSize: typography.fontSize.sm, color: semanticColors.destructive,
            }}
          >
            <AlertCircle size={iconSize.md} strokeWidth={iconStroke.regular}
                         style={{ flexShrink: 0, marginTop: spacing[2] }} />
            <span>Prices could not be updated, so the priced columns are blank rather than estimated. {liveError}</span>
          </div>
        )}

        <DealSection step={dealStep.build} alwaysOpen openSignal={openSignal.build}>
        <div style={{ marginTop: spacing[8], display: "flex", flexDirection: "column" }}>
          <div className="flex items-center justify-between"
               style={{ marginBottom: spacing[8], gap: spacing[12], flexWrap: "wrap" }}>
            <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>Material list</div>
            <div className="flex items-center" style={{ gap: spacing[12] }}>
              <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
                <span data-testid="materials-count" className="tabular-nums">{displayMaterials.length}</span> lines
              </div>
              {/* The user-facing half of what the dev panel's "Recalculate
                  via backend" did: same call, same numbers, wording a
                  salesperson can act on. It stays because without any
                  trigger the priced columns would never fill in — hiding the
                  panel must not take the capability with it. */}
              {canEdit && <button
                data-testid="quote-refresh-pricing"
                className="btn-secondary"
                onClick={runBackendPricing}
                disabled={liveStatus === "loading" || rows.length === 0}
                style={{
                  height: heights.control, padding: `0 ${spacing[12]}px`,
                  backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: spacing[6],
                  opacity: liveStatus === "loading" || rows.length === 0 ? 0.6 : 1,
                }}
              >
                <RefreshCw
                  size={iconSize.md}
                  strokeWidth={iconStroke.regular}
                  className={liveStatus === "loading" ? "animate-spin" : undefined}
                />
                {liveStatus === "loading" ? "Updating prices…" : "Update prices"}
              </button>}
            </div>
          </div>

          {displayMaterials.length === 0 ? (
            <div
              data-testid="materials-empty-state"
              className="flex flex-col items-center justify-center"
              style={{ flex: 1, minHeight: 0, gap: spacing[12] }}
            >
              <div style={{ fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder }}>
                No materials yet
              </div>
              {canEdit && <button
                data-testid="quote-add-materials"
                className="btn-primary"
                onClick={() => setPickerOpen(true)}
                style={{
                  height: heights.control, padding: `0 ${spacing[14]}px`,
                  backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                  border: "none", borderRadius: radius.base,
                  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
                }}
              >
                Add Materials
              </button>}
            </div>
          ) : (
            <>
              <SummaryStrip
                brandIssues={warnings.brandViolations}
                inStock={warnings.inStock}
                stockUnknown={warnings.stockUnknown}
                showOnlyIssues={showOnlyIssues}
                showOnlyInStock={showOnlyInStock}
                onToggleIssues={() => setShowOnlyIssues((v) => !v)}
                onToggleInStock={() => setShowOnlyInStock((v) => !v)}
              />

              {/* oem-quote-register: the amber OEM warnings, one per line,
                  on a draft only. Never blocking. */}
              {!frozen && (() => {
                const items = rows.flatMap((r) => oemWarningsFor(r).map((w) => ({ r, w })));
                if (!items.length) return null;
                return (
                  <div data-testid="oem-warnings" role="status" style={{
                    margin: `${spacing[8]}px 0`, padding: `${spacing[8]}px ${spacing[12]}px`,
                    border: `1px solid ${semanticColors.warningBorder}`, borderRadius: radius.base,
                    backgroundColor: semanticColors.warningBg, color: colors.amber800,
                    fontSize: typography.fontSize.sm, display: "flex", flexDirection: "column", gap: spacing[4],
                  }}>
                    {items.map(({ r, w }) => (
                      <div key={`${r.lineNo}-${w.kind}`} data-testid={`oem-warning-${r.lineNo}-${w.kind}`}>
                        Line {r.lineNo} ({r.partNo}): {w.text}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {oemLineNo !== null && (() => {
                const line = rows.find((r) => r.lineNo === oemLineNo);
                if (!line) return null;
                return (
                  <OemQuoteDialog
                    line={line}
                    quoteId={draftId ?? quote?.odooQuoteId ?? (isSavedMode && saved?.is_draft ? saved.quote_id : undefined)}
                    onPick={(m) => pickOemMatch(line.lineNo, m)}
                    onUnlink={() => stopUsingOem(line.lineNo)}
                    onClose={() => setOemLineNo(null)}
                  />
                );
              })()}

              <div>
                <MaterialTable
                  key={materialsReloadKey}
                  initial={displayMaterials}
                  onChangeCount={setChangeCount}
                  onRowsChange={setRows}
                  undoRef={undoRef}
                  showOnlyIssues={showOnlyIssues}
                  showOnlyInStock={showOnlyInStock}
                  flashLineNos={flashLineNos}
                  livePricing={livePricing}
                  liveStatus={liveStatus}
                  pricingMode={pricingMode}
                  hasSupplierCurrency={!!supplierCurrency}
                  // The Shipment column's options, and whether it offers a
                  // dropdown at all. The table WRITES the assignment itself --
                  // see MaterialTable.canAssignShipments for why this is a
                  // gate and not a callback.
                  shipments={shipments}
                  canAssignShipments={canEdit && !frozen}
                  onExplainLine={setExplainLineNo}
                  explainedLineNo={explainLineNo}
                  // Gated on canEdit like every other mutating control, so a
                  // Viewer never sees the button at all. Passing undefined is
                  // what removes it — the table renders no control without a
                  // handler.
                  onRemoveLine={canEdit && !frozen ? handleRemoveLine : undefined}
                  onUseOemQuote={canEdit && !frozen ? setOemLineNo : undefined}
                  frozen={frozen}
                  // Disabled at the last line rather than hidden. See the
                  // prop's own comment: the backend refuses a save with no
                  // lines, so deleting to empty would strand the last one.
                  canRemoveLines={rows.length > 1}
                  filter={{ text: lineFilter, onText: setLineFilter }}
                  onClearViewToggles={() => { setShowOnlyIssues(false); setShowOnlyInStock(false); }}
                  pager={{
                    page: linePage, pageSize: linePageSize,
                    onPage: setLinePage, onPageSize: setLinePageSize,
                    reveal: revealLine, onRevealHandled: () => setRevealLine(null),
                  }}
                />
              </div>
            </>
          )}
        </div>

        </DealSection>

        {/* These notifications are deliberately in normal document flow.
            The whole table -- rows, totals and its horizontal scrollbar --
            sits above this stack in the same scrolling column, so none of it
            can ever sit underneath a notification. Two independent Undos remain because they restore
            different state — see RemovedLineToast's own comment. */}
        {(removedLine || changeCount > 0) && (
          <ChangeNotificationStack>
            {removedLine && (
              <RemovedLineToast
                lineNo={removedLine.row.lineNo}
                partNo={removedLine.row.partNo}
                onUndo={undoRemoveLine}
                onDismiss={() => setRemovedLine(null)}
              />
            )}
            <UndoBar count={changeCount} onUndo={() => undoRef.current?.()} />
          </ChangeNotificationStack>
        )}

        {/* ux-guided-deal: THE JOURNEY, in order. The current step's section is
            open; the others collapse to their summary line but stay mounted,
            so every control is one click away and every panel keeps reading. */}
        <DealSection step={dealStep.oem} openSignal={openSignal.oem}>
          {oemQuoteId === undefined ? null : (
            <>
              {/* M2: every OEM offer per line, and Compare. */}
              {deal.offers && <OemOffersSummary lines={deal.offers.lines} onCompare={setCompareLine} />}
              {/* O3: price requests -- asking the OEM, recorded here. */}
              <OemRequestsBlock quoteId={oemQuoteId} data={deal} />
              {/* Order Management O1/O2: what each OEM has confirmed, per line,
                  against the cost this quote used. Shown, never applied. Absent
                  for a caller who may not see this quote's confirmations. */}
              <OemConfirmationsPanel quoteId={oemQuoteId} reloadKey={oemReload}
                onView={onOemView} openRecord={openRecord} />
            </>
          )}
        </DealSection>

        <DealSection step={dealStep.finalise} openSignal={openSignal.finalise} header={
          <>
            {/* margin-approval: the refusal, the pending lock, or a rejection. */}
        {quote && (
          <ApprovalPanel
            approval={quote.approval}
            refusal={approvalRefusal}
            isAdmin={adminView.isAdmin}
            isOwner={quote.ownerUid === adminView.uid}
            busy={approvalBusy}
            error={approvalError}
            onRequest={() => { void requestApprovalNow(); }}
            onWithdraw={() => { void runApprovalAction(withdrawApproval, "The request was not withdrawn."); }}
            onApprove={() => { void runApprovalAction(approveQuote, "The quote was not approved."); }}
            onReject={(reason) => { void runApprovalAction((qid) => rejectQuote(qid, reason), "The request was not rejected."); }}
          />
        )}

          </>
        }>
          <div data-testid="deal-finalise-help" style={{
            marginTop: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
          }}>
            {alreadyFinal
              ? "Finalised: the quote has its number and its prices are locked. Print or download it from Quote Analysis."
              : "Finalise quote (top right) takes the next quote number and locks the prices. It cannot be undone. Below the approval limit it asks an Admin first."}
          </div>
        </DealSection>

        <DealSection step={dealStep.decision} openSignal={openSignal.decision} header={<>
        {/* quote-won-lost: a finalised quote's outcome. Screen only. */}
        {quote && poQuoteId !== undefined && outcomeNow?.status && (
          <OutcomePanel
            outcome={outcomeNow}
            canMark={canEditByRole}
            isAdmin={adminView.isAdmin}
            busy={outcomeBusy}
            error={outcomeError}
            quoteId={poQuoteId}
            onWon={(dates) => { void runOutcomeAction((qid) => markQuoteWon(qid, dates), "The quote was not marked won."); }}
            onLost={(reason, note) => {
              void runOutcomeAction((qid) => markQuoteLost(qid, reason, note), "The quote was not marked lost.");
            }}
            onReset={() => { void runOutcomeAction(resetQuoteStatus, "The status was not reset."); }}
            openWon={openWon}
            openLost={openLost}
          />
        )}
        </>} />

        <DealSection step={dealStep.order} openSignal={openSignal.order}>
          {/* O4/O5: the ordering gate (asked, never decided here) and placing. */}
          {oemQuoteId !== undefined && (
            <OemOrderingBlock quoteId={oemQuoteId} data={deal} reloadKey={oemReload}
              onRefusals={onGateRefusals} />
          )}
        </DealSection>

        <DealSection step={dealStep.track} openSignal={openSignal.track}>
          {/* O6/O7: each order's events, status and the order status by line. */}
          {oemQuoteId !== undefined && (
            <OemTrackingBlock quoteId={oemQuoteId} data={deal} reloadKey={oemReload}
              openOrder={openOrder} />
          )}
        </DealSection>

        {/* M2: the price comparison for one line (a portal). */}
        {compareLine !== null && oemQuoteId !== undefined && (() => {
          const olNow = deal.offers?.lines.find((x) => x.line_id === compareLine);
          const rowNow = olNow ? rows.find((r) => r.lineNo === olNow.line_no) : undefined;
          if (olNow && rowNow) compareShown.current = { ol: olNow, row: rowNow };
          else if (compareShown.current?.ol.line_id !== compareLine) compareShown.current = null;
          if (!compareShown.current) return null;
          const { ol, row } = compareShown.current;
          return (
            <OemCompareDialog quoteId={oemQuoteId} line={ol} row={row} mode={pricingMode}
              supplierCurrency={supplierCurrency} gpPct={gpPct} variancePct={VARIANCE_PCT}
              canAct={canEdit && !frozen}
              onUseOemQuote={(m) => pickOemMatch(ol.line_no, m)}
              onChanged={deal.refresh} onClose={() => setCompareLine(null)} />
          );
        })()}

        {/* M4 (O8-O11, D195): receive into our warehouse, deliver, close out.
            Steps 7 and 8 exist only when an order comes through our warehouse. */}
        {dealStep.receive && (
          <DealSection step={dealStep.receive} openSignal={openSignal.receive}>
            {oemQuoteId !== undefined && !fulfil.hidden && (
              <>
                <ReceiveGoodsBlock orders={deal.orders} data={fulfil} canRecord={!!dealView?.can_record} />
                <CustomsBlock orders={deal.orders} data={fulfil} canRecord={!!dealView?.can_record} />
              </>
            )}
          </DealSection>
        )}
        {dealStep.deliver && (
          <DealSection step={dealStep.deliver} openSignal={openSignal.deliver}>
            {oemQuoteId !== undefined && !fulfil.hidden && (
              <DeliverBlock quoteId={oemQuoteId} orders={deal.orders} data={fulfil}
                canRecord={!!dealView?.can_record} closed={!!fulfil.status?.closed} />
            )}
          </DealSection>
        )}
        <DealSection step={dealStep.close} openSignal={openSignal.close}>
          {oemQuoteId !== undefined && !fulfil.hidden && deal.orders.length > 0 && (
            <>
              {/* A drop-ship-only deal records customs here (no step 7). */}
              {!dealStep.receive && <CustomsBlock orders={deal.orders} data={fulfil} canRecord={!!dealView?.can_record} />}
              <CloseOutBlock quoteId={oemQuoteId} data={fulfil} isAdmin={adminView.isAdmin} />
            </>
          )}
        </DealSection>
      </div>

      {/* Per-line price explanation. An inline aside like the other
          record-detail panels — conditionally rendered, unmounts on close —
          sitting between the table and the Quote Analysis rail, so the chain
          it explains reads left-to-right out of the row it came from.

          It COEXISTS with SidePanel rather than replacing it. Opening an
          explanation collapses that panel to its rail (see the effect above);
          it stays mounted, keeps its accordion state and its in-progress
          keystroke buffers, and its ctx controls are one click away. */}
      {explainOpen && (
        <PriceExplainPanel
          line={explainLine}
          priced={explainPriced}
          mode={pricingMode}
          onClose={() => setExplainLineNo(null)}
        />
      )}

      {/* Right side panel */}
      <SidePanel
        rows={rows}
        ctx={ctx}
        onChangeCtx={(updater) => setCtx((prev) => updater(prev))}
        collapsed={panelCollapsed}
        onToggle={() => {
          // A manual toggle hands the rail back to the user: whatever this
          // page auto-collapsed is no longer ours to restore, so closing the
          // explanation later must not undo their choice.
          autoCollapsedRail.current = false;
          setPanelCollapsed((v) => !v);
        }}
        onGeneratePdf={() => setPdfPreviewOpen(true)}
        stacked={narrow}
        // The shipment editor's gating pair, mirroring what MaterialTable
        // already receives: import mode is what makes the freight section
        // exist at all, and the currency decides whether a shipment can be
        // created in it. canEdit gates every mutating control, like the
        // remove-line button and the currency select above.
        pricingMode={pricingMode}
        // Engine results, so the domestic margin section reads validated
        // numbers rather than the placeholder row fields.
        livePricing={livePricing}
        shipments={shipments}
        supplierCurrency={supplierCurrency ?? ""}
        // A finalised quote's shipments feed its freight, so they are locked.
        canEdit={canEdit && !frozen}
        onChangeShipments={setShipments}
      />

      {pickerOpen && (
        <MaterialPicker
          quote={quote}
          storeKey={storeKey}
          onClose={() => setPickerOpen(false)}
          onAdded={handleMaterialsAdded}
        />
      )}

      {customerDetailsOpen && quote.customer?.entity?.id && (
        <CustomerDetailsModal
          entity={quote.customer.entity}
          onClose={() => setCustomerDetailsOpen(false)}
          onSaved={(saved, note) => {
            setEditedEntity(saved); setCustomerDetailsOpen(false); setCustomerGstinNote(note ?? "");
          }}
        />
      )}

      {pdfPreviewOpen && (
        <QuotePdfPreview
          // The customer's details as just edited here, when they are this
          // quote's customer; otherwise the quote exactly as read.
          //
          // ⚠️ A SNAPSHOT, WHEN THERE IS ONE, IS WHAT PRINTS -- the document
          // ignores the live customer then, so neither override below can
          // reach a finalised quote's printed details.
          quote={{
            ...(editedEntity && quote.customer?.entity?.id === editedEntity.id
              ? { ...quote, customer: { ...quote.customer, entity: editedEntity } }
              : quote),
            customerSnapshot: issuedSnapshot ?? quote.customerSnapshot,
          }}
          materials={rows}
          // The engine numbers. Without this the document falls back to
          // pricing.ts placeholders -- which is what printed ~22% low.
          livePricing={livePricing}
          quoteName={savedName}
          company={companySettings}
          tax={pdfTax}
          taxPending={poQuoteId !== undefined && pdfTaxPending}
          onClose={() => setPdfPreviewOpen(false)}
        />
      )}
    </div>
  );
}
